import {
  FrameLabel,
  Image,
  MediaDetailObservation,
  MediaDetailPrediction,
  MediaDetailResponseDto,
  ResolvedSuggestion,
} from "../common/ObjectTypes";
import { MediaDetailNavigationContext } from "../common/QueryTypes";
import { useTranslation } from "react-i18next";
import {
  LinkIcon,
  PrevArrow,
  NextArrow,
  BrightMode,
  QuestionCircleIcon,
} from "../common/ProjectIcons";
import { Tooltip } from "@mui/material";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import { toast } from "react-toastify";
import { getMediaDetail } from "../../api/deploymentsApi";
import {
  acceptAllSuggestions,
  acceptSelectedCards,
  assignSpeciesToCards,
  acceptSuggestion,
  createObservation,
  deleteObservation,
  editSuggestion,
  markBlank,
  rejectSelectedCards,
  rejectSuggestion,
  saveMedia,
  updateObservation,
} from "../../api/reviewActionsApi";
import ConfirmationModal from "../common/ConfirmationModal";
import { useProfile } from "../common/ProfileProvider";
import { isTopLayerOpen } from "../common/topLayer";
import {
  DetailBox,
  boxIdsForOwner,
  liveOwnerKeys,
  suggestionOwnerKey,
  composeDetailBoxes,
  composeFrameLabels,
  deriveCardState,
  isResolvedSuggestion,
  pickPrimaryLabel,
  predictionsById,
} from "./mediaDetailComposer";
import {
  CandidateSelection,
  applyCandidateSelection,
} from "./candidateSpecies";
import {
  EditFields,
  MarkBlankTrigger,
  SpeciesOption,
  buildObservationUpdateRequest,
  findExistingComment,
  findParentTaxonId,
  isAllowedWhileCommitting,
  isMarkBlankConfirmHotkey,
  isZeroObservationsError,
  markBlankPreflight,
  newIdempotencyKey,
  shouldAdvanceAfterMarkBlank,
} from "./reviewActions";
import ImageCanvas, { ImageCanvasHandle } from "./ImageCanvas";
import { BoxClickAnchor } from "./BoundingBoxOverlay";
import SuggestionCard, { SuggestionReviewState } from "./SuggestionCard";
import {
  AssignSpeciesCardPayload,
  assignSelection,
  selectedBoxIds as assignSelectedBoxIds,
} from "./assignSpeciesSelection";
import ObservationList from "./ObservationList";
import ObservationEditor, {
  ObservationEditorInitial,
} from "./ObservationEditor";
import PredictionPopover from "./PredictionPopover";
import ReviewQueueStrip from "./ReviewQueueStrip";
import { buildImageFilter, SHARPEN_FILTER_ID } from "./imageFilter";

const MIN_BRIGHTNESS = 0.5;
const MAX_BRIGHTNESS = 2.0;
const DEFAULT_BRIGHTNESS = 1.0;

/** Which editor is open and what it commits on confirm. */
type EditorState = {
  context:
    | "aggregate-edit"
    | "object-edit"
    // AKL-717: name a first species for an `unresolved` box — an object edit
    // with no original taxon.
    | "object-assign"
    | "add"
    | "obs-edit"
    | "assign-species";
  title: string;
  initial: ObservationEditorInitial;
  // Suggestion/object edit targets (design §7.3 anchors to prediction_ids).
  predictionIds?: number[];
  originalTaxonId?: number;
  parentTaxonId?: number;
  // Existing-observation edit target.
  observationId?: number;
  // AKL-774 assign-species target: every ticked card, in panel order, each with
  // its own original taxon so the server can record accept-vs-edit per card.
  assignCards?: AssignSpeciesCardPayload[];
  // How many cards were ticked. Every {{count}} in this flow's copy is a card
  // count, while the editor's count field is prefilled from the BOX count -
  // two different numbers, which is why both are carried explicitly.
  assignCardCount?: number;
  // Note rendered under the count field, explaining where its prefill came from.
  countHelp?: string;
  // AKL-781: the distinct taxa already on the ticked cards, offered as the
  // species picker's initial options so the AI's own answer can be picked
  // instead of retyped. An offer only - `initial.taxonId` stays null.
  speciesOptions?: SpeciesOption[];
  // AKL-774: minted once when the assign-species editor opens and reused for
  // every attempt it makes. A failed commit keeps the mode, the ticks AND the
  // editor open, so a retry is expected — and a fresh key per attempt would turn
  // an ambiguous timeout (first request committed, response lost) into a second
  // re-action instead of a replay.
  assignIdempotencyKey?: string;
};

/** A pending confirmation the surface must resolve before proceeding. */
type ConfirmState = {
  kind: "delete-obs" | "mark-blank" | "zero-obs";
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
};

/**
 * Identifies which control triggered the in-flight commit so that only that
 * button shows a spinner — `actionBusy` alone is shared across every commit
 * control. Suggestion accept/reject carry the card's aggregation key so the
 * spinner lands on the exact card acted on (incl. via the A/R shortcuts);
 * editor- and confirm-modal-driven actions carry no BusyAction because those
 * surfaces render their own spinner.
 *
 * `accept-all` is whole-photo, not per-card (AKL-752): the bulk accept is a
 * single transactional commit over every pending card, so its spinner belongs
 * on the Actions-panel button. The card-keyed kinds are still exactly right for
 * a single-card accept or reject.
 */
type BusyAction =
  | { kind: "save" }
  | { kind: "save-next" }
  | { kind: "accept-all" }
  | { kind: "suggestion-accept"; key: string }
  | { kind: "suggestion-reject"; key: string }
  | { kind: "object-accept" }
  | { kind: "object-reject" };

/**
 * Format an ISO timestamp as `YYYY-MM-DD HH:mm:ss` for the top-bar readout,
 * falling back to an empty string when absent or invalid.
 * @param iso the ISO 8601 string, or null
 * @returns the formatted local timestamp
 */
const formatCaptureTime = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

/**
 * Full-screen single-photo review surface (AKL-681, read-only), styled after the
 * Phase 2 review-panel mockup: a full-screen "theater" (light/dark theme-aware)
 * with a top info bar, a left tool rail + image (shared ImageCanvas:
 * zoom/pan/magnifier) with AI bounding-box overlays coloured by review state and
 * visibility, a right "Review panel" (AI-suggested and rejected predictions plus
 * existing observations — out-of-list and unresolved boxes live on the image
 * only, AKL-717), and a bottom thumbnail queue strip.
 * Stepping (arrows + on-screen prev/next + strip) walks the
 * in-memory feed via `onStep`/`onSelectIndex`; the server navigation block
 * drives the progress readout. Commit actions (AKL-682 onwards) live on the
 * cards, the box popover and the Actions panel; AKL-717 adds Assign species
 * for an unresolved box.
 *
 * @param image the opened feed image (deep-link fields + instant photo url)
 * @param mediaId the media_asset_id to load detail for
 * @param projectId the project scope for the species picker
 * @param navigationContext the active filter/sort for server-side progress
 * @param images the current feed page (for the thumbnail strip)
 * @param currentIndex the opened photo's index within `images`
 * @param position the opened photo's 1-based position in the filtered set
 * @param total the total number of photos in the filtered set
 * @param canStepPrev whether a previous photo exists in the feed
 * @param canStepNext whether a next photo exists in the feed
 * @param onStep steps to the previous (-1) or next (+1) photo
 * @param onSelectIndex opens the photo at the given index within `images`
 * @param onClose closes the full-screen view
 */
const FullScreenImageReview = ({
  image,
  mediaId,
  projectId,
  navigationContext,
  images,
  currentIndex,
  position,
  total,
  canStepPrev,
  canStepNext,
  onStep,
  onSelectIndex,
  onClose,
}: {
  image: Image;
  mediaId: number;
  projectId: number;
  navigationContext: MediaDetailNavigationContext | null;
  images: Image[];
  currentIndex: number;
  position: number;
  total: number;
  canStepPrev: boolean;
  canStepNext: boolean;
  onStep: (direction: -1 | 1) => void;
  onSelectIndex: (index: number) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { theme } = useProfile();
  const [titleKey, setTitleKey] = useState<string>("Copy link");
  const navigate = useNavigate();
  // Spinner color: pale sky-blue reads well on the dark theater but washes out
  // on the light panel, so use the dark navy in light mode.
  const spinnerColor = theme === "dark" ? "#B0E6FA" : "#272837";

  const [detail, setDetail] = useState<MediaDetailResponseDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // AKL-698: card ↔ bbox linkage state. Separate from `selectedBoxId` (which
  // drives the prediction popover) so card interactions never open the popover.
  // `highlightedBoxIds` is the transient list of box ids the reviewer's current
  // card interaction (hover or persistent click) is linked to; `selectedCardKey`
  // is the persistent-selected card identity that survives until another card
  // is picked or Escape is pressed.
  const [highlightedBoxIds, setHighlightedBoxIds] = useState<string[]>([]);
  const [selectedCardKey, setSelectedCardKey] = useState<string | null>(null);
  // AKL-698: box ids for the persistently-selected card, tracked separately
  // from the transient `highlightedBoxIds` so mouse-leave over a different
  // card can restore the selection's highlight instead of leaving whatever
  // was hovered last on screen.
  const [selectedCardBoxIds, setSelectedCardBoxIds] = useState<string[]>([]);
  // AKL-774: assign-species selection mode. Deliberately separate from the
  // AKL-698 single-card `selectedCardKey` rather than generalising it: the two
  // mean different things (one drives a highlight, the other drives a commit),
  // and collapsing them would make leaving the mode ambiguous.
  const [assignMode, setAssignMode] = useState(false);
  const [selectedSuggestionKeys, setSelectedSuggestionKeys] = useState<
    Set<string>
  >(new Set());
  // AKL-698: reviewer-toggled label visibility while a card is pointer-selected.
  // Starts on when a selection is opened; the reviewer can uncheck it to focus
  // on the box outlines alone. Reset back to `true` when the selection clears
  // or the surface unmounts.
  const [showBoxLabels, setShowBoxLabels] = useState(true);
  // AKL-698: optimistic reviewed markers. As soon as Save / Save & Next fires
  // we mark the current photo reviewed locally so the top-bar chip and the
  // filmstrip badge flip immediately; the server truth arrives via the detail
  // refetch shortly after and takes over. Keyed by the string `Image.id` so
  // both the current photo and any previously-saved photos on the strip stay
  // flipped as the reviewer moves through the feed.
  const [optimisticallyReviewedImageIds, setOptimisticallyReviewedImageIds] =
    useState<Set<string>>(new Set());
  // A review action (accept/reject/edit/etc.) refreshes this surface's own
  // `detail` state via applyDetail, which correctly recomposes the main
  // viewer's `boxes` (useMemo below) -- but the film strip renders from the
  // `images` prop passed down from the parent feed, composed once at
  // fetch time, which applyDetail never touches. Without this override the
  // strip's thumbnail for the open photo keeps showing pre-action boxes
  // (e.g. a just-rejected prediction's box) until the reviewer closes the
  // surface and the parent refetches. Keyed by `Image.id` (a string),
  // matching `optimisticallyReviewedImageIds` above, so it merges into the
  // same `images` prop in one pass.
  const [boxOverridesByImageId, setBoxOverridesByImageId] = useState<
    Map<string, { boxes: DetailBox[]; frameLabels: FrameLabel[] }>
  >(new Map());
  const [zoomPercent, setZoomPercent] = useState(100);
  const [magnifierOn, setMagnifierOn] = useState(false);
  const [showBoxes, setShowBoxes] = useState(true);
  // Image-adjustment state (AKL-681). Persists across photo steps within the
  // session (deliberately NOT reset in the per-mediaId effect) and is not
  // written to localStorage — it unmounts with the surface.
  const [brightness, setBrightness] = useState(DEFAULT_BRIGHTNESS);
  const [sharpen, setSharpen] = useState(false);
  const [brightnessPopoverOpen, setBrightnessPopoverOpen] = useState(false);
  // Covers BOTH the trigger button and the popover so an outside-click listener
  // doesn't fight the toggle button (mousedown closes + subsequent click reopens).
  const brightnessControlRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<ImageCanvasHandle>(null);

  // --- Review-action state (AKL-682) ---------------------------------------
  const [actionBusy, setActionBusy] = useState(false);
  // Which commit initiated the in-flight action, so only the button the
  // reviewer clicked shows a spinner (actionBusy alone is shared across every
  // commit control). Cleared automatically whenever actionBusy returns false.
  const [busyAction, setBusyAction] = useState<BusyAction | null>(null);
  useEffect(() => {
    if (!actionBusy) setBusyAction(null);
  }, [actionBusy]);
  // The control that started the last commit able to end in the zero-observation
  // confirm (Save, Save & Next, Accept all). That confirm opens only after the
  // request fails, while the control is disabled; on QA focus had left it for
  // <body> by then, so the dialog had no opener of its own to hand focus back
  // to on close. It uses this instead (AKL-883, QA Swarm UX-002).
  const commitOpenerRef = useRef<HTMLElement | null>(null);
  /** Remember what has focus now, before a commit disables it. */
  const rememberCommitOpener = () => {
    commitOpenerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
  };
  /** Which button (if any) on the suggestion card `key` is mid-commit. */
  const suggestionPending = (key: string): "accept" | "reject" | null =>
    busyAction?.kind === "suggestion-accept" && busyAction.key === key
      ? "accept"
      : busyAction?.kind === "suggestion-reject" && busyAction.key === key
        ? "reject"
        : null;
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [cardFocusIndex, setCardFocusIndex] = useState<number | null>(null);

  // Read the navigation context from a ref so filter-object identity churn does
  // not re-trigger the detail fetch; the fetch keys on mediaId.
  const navigationContextRef = useRef(navigationContext);
  navigationContextRef.current = navigationContext;

  // Track the currently-open media id so an in-flight action's response can be
  // dropped if the reviewer already navigated to a different photo (AKL-682).
  const mediaIdRef = useRef(mediaId);
  mediaIdRef.current = mediaId;

  // Read by `applyDetail`, which is dependency-free so a fresh response never
  // re-creates it. Refs rather than dependencies, and rather than setState
  // updaters with side effects inside them.
  const selectedCardKeyRef = useRef<string | null>(selectedCardKey);
  selectedCardKeyRef.current = selectedCardKey;
  const selectedSuggestionKeysRef = useRef(selectedSuggestionKeys);
  selectedSuggestionKeysRef.current = selectedSuggestionKeys;

  // Session-scoped media-detail cache, keyed by media id (AKL-707). Avoids
  // re-fetching /media-assets/detail when stepping back to a photo already
  // viewed this session. Scoped to this component's mount lifetime — a fresh
  // Map on every mount, not persisted across a close/reopen.
  const detailCacheRef = useRef<Map<number, MediaDetailResponseDto>>(new Map());

  /**
   * Navigate to the image route when the image changes.
   */
  useEffect(() => {
    if (image.projectGuid && image.deploymentGuid && image.name) {
      navigate(
        `/review/${image.projectGuid}/${image.deploymentGuid}/image/${image.name}`,
        { replace: true },
      );
    }
  }, [image.projectGuid, image.deploymentGuid, image.name, navigate]);

  /**
   * Fetch the resolved media detail whenever the opened media changes. Stale
   * responses are ignored so fast prev/next stepping cannot render mismatched
   * data. Reads through the session-scoped detail cache (AKL-707) first, so
   * stepping back to a photo already fetched this session skips the network
   * round-trip entirely.
   */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setSelectedBoxId(null);
    setPopoverAnchor(null);
    // Reset per-photo action state so nothing leaks across a step.
    setEditor(null);
    setConfirm(null);
    setCardFocusIndex(null);
    setActionBusy(false);
    setHighlightedBoxIds([]);
    setSelectedCardKey(null);
    setSelectedCardBoxIds([]);
    setShowBoxLabels(true);
    // The ticked keys belong to the photo being left; carrying them would let a
    // commit act on cards the reviewer can no longer see.
    setAssignMode(false);
    setSelectedSuggestionKeys(new Set());
    // Clear the previous photo's detail so its boxes don't briefly overlay the
    // new image during fast prev/next stepping.
    setDetail(null);

    const cached = detailCacheRef.current.get(mediaId);
    if (cached) {
      setDetail(cached);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    getMediaDetail({
      media_id: mediaId,
      navigation: navigationContextRef.current,
    })
      .then((resp) => {
        if (cancelled) return;
        detailCacheRef.current.set(mediaId, resp);
        setDetail(resp);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mediaId]);

  /**
   * Reset tooltip title when image changes.
   */
  useEffect(() => {
    setTitleKey("Copy link");
  }, [image]);

  const imageFilter = useMemo(
    () => buildImageFilter({ brightness, sharpen }),
    [brightness, sharpen],
  );

  /**
   * AKL-777: the species the reviewer has picked in the box popover, per box.
   *
   * Keyed by BOX id, not prediction id: the reviewer is choosing a species for
   * a box, and which predictions carry a box's label can change under a detail
   * refresh. The display name is stored alongside the taxon so `boxes` below
   * can relabel without rebuilding every box's candidate rows.
   *
   * Pending only - nothing is written until Accept - so this is surface state
   * and dies with the surface.
   */
  const [candidateTaxonByBox, setCandidateTaxonByBox] = useState<
    Record<string, CandidateSelection>
  >({});

  const composedBoxes = useMemo(() => composeDetailBoxes(detail), [detail]);
  // Overlay any pending species choice onto the composed boxes, so the image
  // shows what Accept would commit BEFORE the reviewer commits it. Applied here
  // rather than inside `composeDetailBoxes` because selection only exists on
  // this surface - the grid and film strip compose from the same function and
  // have no popover to pick in.
  const boxes = useMemo(
    () => applyCandidateSelection(composedBoxes, candidateTaxonByBox),
    [composedBoxes, candidateTaxonByBox],
  );
  const predictionMap = useMemo<Map<number, MediaDetailPrediction>>(
    () => (detail ? predictionsById(detail) : new Map()),
    [detail],
  );

  // AKL-698 debug: log the raw suggestion/observation payload and the composed
  // overlay boxes whenever the photo detail changes, so the review data (incl.
  // each prediction's visibility) can be inspected in the browser console.
  // Dev-only — never logs (potentially sensitive) review data in production.
  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    if (!detail) return;
    console.log("[review] suggested observations:", detail.suggestions);
    console.log("[review] observations:", detail.observations);
    console.log(
      "[review] overlay boxes:",
      boxes,
      "| predictions:",
      detail.predictions,
    );
  }, [detail, boxes]);
  // AKL-698: split visible suggestions into pending (unreviewed) and rejected
  // groups. Accepted / edited clusters are absorbed into `detail.observations`
  // and render there instead of duplicating in the suggestions section.
  // AKL-703: the panel sections are driven by the card's server-assigned
  // disposition. Previously "rejected" was inferred client-side from the cluster's
  // predictions via deriveCardState — which silently stopped matching anything
  // once AKL-700 dropped every reviewed prediction from the resolve pass, so the
  // Rejected section could never populate. The API now aggregates rejected
  // clusters separately and tags them, so each section reads one field.
  // AKL-717: both memos narrow to `ResolvedSuggestion` — an `unresolved` box has
  // no species and is never a card, so nothing downstream has to null-check a
  // card's taxon. The type guard is what TypeScript needs; the visibility test
  // alone would keep the union.
  const visibleSuggestions = useMemo(
    () =>
      (detail?.suggestions ?? []).filter(
        (s): s is ResolvedSuggestion =>
          isResolvedSuggestion(s) && s.visibility === "visible",
      ),
    [detail],
  );
  const rejectedSuggestions = useMemo(
    () =>
      (detail?.suggestions ?? []).filter(
        (s): s is ResolvedSuggestion =>
          isResolvedSuggestion(s) && s.visibility === "rejected",
      ),
    [detail],
  );
  // AKL-774: every card a selection may contain, in panel order. Pending and
  // rejected mix freely (Margaux, 2026-08-31); excluded cards are not tickable.
  const selectableSuggestions = useMemo(
    () => [...visibleSuggestions, ...rejectedSuggestions],
    [visibleSuggestions, rejectedSuggestions],
  );
  // AKL-781: whether select-suggestions mode can be entered at all. One value
  // drives both the Select suggestions button's render and the `M` hotkey's
  // guard, so the two cannot drift apart — a hotkey that entered the mode on a
  // photo with no tickable cards would strand the reviewer in a mode with
  // nothing to select and no visible control to leave by, since the button is
  // what turns it back off.
  const canEnterAssignMode = selectableSuggestions.length > 0;
  // AKL-784 (review): whether the mode's control renders and its hotkey fires.
  // Deliberately not the same as `canEnterAssignMode` — the tickable set can
  // empty *while the mode is on* (accept-all consumes every pending card), and
  // the button is the only pointer route out, so it has to survive that.
  const canToggleAssignMode = assignMode || canEnterAssignMode;
  // The ticked set. Panel order, so the request is reproducible regardless of
  // the order the reviewer clicked in.
  const assignSelected = useMemo(
    () => assignSelection(selectableSuggestions, selectedSuggestionKeys, boxes),
    [selectableSuggestions, selectedSuggestionKeys, boxes],
  );
  // Count the boxes actually drawn — visible, out-of-list and AKL-717's
  // unresolved alike — so the summary agrees with what the reviewer sees.
  const detectionCount = boxes.length;

  // The popover shows every label-bearing prediction on the selected box
  // (AKL-777). It used to be a single prediction - the label source - which is
  // what made a second classifier's answer unreachable from the image.
  const selectedBoxPredictions: MediaDetailPrediction[] = useMemo(() => {
    if (selectedBoxId == null) return [];
    // Looked up from `boxes` rather than the `selectedBox` memo, which is
    // declared further down this component.
    const box = boxes.find((b) => b.id === selectedBoxId);
    if (!box) return [];
    return box.labelPredictionIds
      .map((id) => predictionMap.get(id))
      .filter((p): p is MediaDetailPrediction => p != null);
  }, [selectedBoxId, boxes, predictionMap]);

  const selectedBoxCandidateTaxon =
    selectedBoxId != null
      ? candidateTaxonByBox[selectedBoxId]?.taxonId
      : undefined;

  /**
   * Get the route to the image.
   * @param image The image to get the route for.
   * @returns The route to the image
   */
  const getImageRoute = (image: Image) => {
    return (
      window.location.origin +
      `/review/${image.projectGuid}/${image.deploymentGuid}/image/${image.name}`
    );
  };

  // image.name arrives URL-encoded from the API (derived from blob URI segment).
  const displayName = (() => {
    try {
      return decodeURIComponent(image.name);
    } catch {
      return image.name;
    }
  })();

  const captureText = formatCaptureTime(
    detail?.capture_time_actual ?? image.captureTime,
  );
  const deploymentName = detail?.deployment?.name || image.deployment;
  const locationName = detail?.location?.name || image.location;
  const sensorName = detail?.sensor?.name || image.sensor;
  // AKL-696 / AKL-698: reviewed state for the top-bar chip. Server truth
  // (detail payload) is authoritative once it arrives, but the optimistic
  // marker set by Save / Save & Next flips the chip immediately on click.
  const isReviewed =
    optimisticallyReviewedImageIds.has(image.id) ||
    (detail?.review_status ?? image.review_status) === "reviewed";

  const nav = detail?.navigation ?? null;
  const readoutPosition = nav && nav.position != null ? nav.position : position;
  const readoutTotal = nav ? nav.total_in_filter : total;

  // AKL-717: `event` is a mouse event or, for Enter / Space on a focused box,
  // the box's centre — only its client coordinates are read here.
  const handleBoxClick = (boxId: string | null, event: BoxClickAnchor) => {
    if (boxId) {
      setSelectedBoxId(boxId);
      setPopoverAnchor({ x: event.clientX, y: event.clientY });
      // AKL-698: clicking a bbox also selects the card that owns it, so the
      // side panel gives the same visual feedback as clicking the card
      // directly (pointer-selected ring on the row + all other boxes hide).
      // AKL-703: every box records its owning card, so this is a direct read —
      // no matching each suggestion / observation against the box list, and no
      // need to skip clusters that render no card (a committed observation owns
      // its box outright, so a clicked box can only name a rendered card).
      // In assign-species mode the highlight belongs to the ticked cards, and
      // the panel's checkboxes are the source of truth for what is selected.
      // Taking over the single-card selection here would blank the union the
      // reviewer built and show an unticked card as selected — the image and
      // the panel would disagree. The popover above still opens, so a box is
      // still inspectable mid-selection.
      if (assignMode) return;
      const clicked = boxes.find((b) => b.id === boxId);
      // AKL-717 (Copilot web#775): an unresolved or out-of-list box has no card
      // in the panel, so there is nothing to link the click to. Selecting its
      // owner key anyway would leave a phantom card selection behind — the
      // "Show labels + confidence" toggle appearing for a box with no label,
      // every other box hidden as if a card were selected. The popover above
      // is the whole interaction for such a box.
      if (
        clicked &&
        clicked.ownerKind === "suggestion" &&
        (clicked.visibility === "unresolved" ||
          clicked.visibility === "out_of_list")
      ) {
        // ...and a card selected before the click is cleared, exactly as an
        // empty-space click does, so a stale highlight cannot keep hiding the
        // box just clicked or leave the labels toggle up for a card that is
        // no longer the subject (Copilot web#775, round 3).
        setSelectedCardKey(null);
        setSelectedCardBoxIds([]);
        setHighlightedBoxIds([]);
        setShowBoxLabels(true);
        return;
      }
      if (clicked) {
        const matchedBoxIds = boxes
          .filter((b) => b.ownerKey === clicked.ownerKey)
          .map((b) => b.id);
        setSelectedCardKey(clicked.ownerKey);
        setSelectedCardBoxIds(matchedBoxIds);
        setHighlightedBoxIds(matchedBoxIds);
        // Reset the label-visibility toggle to on whenever a new selection
        // opens, so the reviewer always starts from labels-on for a fresh
        // pick (mirrors what handleCardClick and the per-photo effect do).
        setShowBoxLabels(true);
      }
    } else {
      setSelectedBoxId(null);
      setPopoverAnchor(null);
      // Same reasoning as above: in assign-species mode the highlight is the
      // ticked union, and clicking empty space must not silently discard it.
      if (assignMode) return;
      // Empty-space click also clears the card ↔ bbox selection so the
      // panel and image stay in sync. Reset labels-on for the same reason
      // handleCardClick does when it clears.
      setSelectedCardKey(null);
      setSelectedCardBoxIds([]);
      setHighlightedBoxIds([]);
      setShowBoxLabels(true);
    }
  };

  const selectedBox = useMemo(
    () => boxes.find((b) => b.id === selectedBoxId) ?? null,
    [boxes, selectedBoxId],
  );

  // --- Review-action handlers (AKL-682) ------------------------------------

  /** Replace the surface state from a fresh detail response after an action. */
  const applyDetail = useCallback((resp: MediaDetailResponseDto) => {
    setDetail(resp);
    // Recompose this photo's film-strip thumbnail from the same fresh
    // response driving the main viewer, so a just-rejected (or otherwise
    // changed) box disappears from the strip immediately rather than only
    // once the surface closes and the parent's feed refetches.
    // Composed once and reused below by the tick-pruning, rather than twice.
    const freshBoxes = composeDetailBoxes(resp);
    setBoxOverridesByImageId((prev) => {
      const next = new Map(prev);
      next.set(String(resp.media_asset_id), {
        boxes: freshBoxes,
        frameLabels: composeFrameLabels(resp.predictions),
      });
      return next;
    });
    setSelectedBoxId(null);
    setPopoverAnchor(null);
    setEditor(null);
    setConfirm(null);
    setActionBusy(false);
    // AKL-777: a committed action makes every pending species choice on this
    // photo meaningless, and a stale entry would keep relabelling a box that
    // has moved on. Cleared wholesale rather than pruned: the popover is closed
    // by the lines above, so there is no selection on screen to preserve.
    setCandidateTaxonByBox({});

    // Drop the pointer-selected card if the fresh detail no longer contains it.
    //
    // An action routinely changes what a card *is*: accepting or editing one
    // turns it into an observation (a different owner key), and rejecting one
    // moves it from the pending list to the rejected list, which changes its
    // `suggestionOwnerKey`. Without this, `selectedCardKey` keeps naming
    // something that is gone — leaving its boxes highlighted, and the
    // label-visibility toggle (rendered whenever the key is non-null) sitting
    // there for a card nobody can see.
    //
    // Not gated on assign-species mode: entering that mode clears
    // `selectedCardKey`, so this is a no-op while it is on and cannot disturb
    // the ticked union.
    const currentKey = selectedCardKeyRef.current;
    if (currentKey != null) {
      if (!liveOwnerKeys(resp).has(currentKey)) {
        setSelectedCardKey(null);
        setSelectedCardBoxIds([]);
        setHighlightedBoxIds([]);
        setShowBoxLabels(true);
      }
    }

    // Same problem for the assign-species ticks (AKL-774). The card-targeted
    // commits are disabled while the mode is on, but the whole-photo ones in the
    // Actions panel are not, and any of them refreshes the detail. A tick left
    // pointing at a card that has since changed disposition would silently drop
    // out of the payload while its box stayed lit — the image showing a
    // selection the request no longer contains.
    const ticked = selectedSuggestionKeysRef.current;
    if (ticked.size > 0) {
      const live = liveOwnerKeys(resp);
      const pruned = new Set(Array.from(ticked).filter((key) => live.has(key)));
      if (pruned.size !== ticked.size) {
        setSelectedSuggestionKeys(pruned);
      }
      setHighlightedBoxIds(
        assignSelectedBoxIds(
          // AKL-717: only cards can be ticked; an unresolved box never is.
          (resp.suggestions ?? []).filter(isResolvedSuggestion),
          pruned,
          freshBoxes,
        ),
      );
    }
  }, []);

  /**
   * Re-fetch the full media detail after an action. The action endpoints return
   * only a partial `ReviewActionResponse`, so the web re-reads the detail
   * endpoint to refresh predictions/suggestions/boxes/observations from server
   * truth (never re-deriving aggregation). Dropped if the reviewer navigated to
   * a different photo mid-flight — a stale response can never overwrite another
   * photo's detail (audit-critical). Always refreshes the session-scoped detail
   * cache (AKL-707) for this photo regardless of that guard — caching accurate
   * server data is safe even after navigating away, whereas applying it to the
   * currently-visible UI is not — so a later re-visit to this photo reads the
   * post-action state rather than a stale pre-action cached response, even if
   * the reviewer navigated elsewhere before this refresh landed.
   */
  const refreshDetail = useCallback(
    (startedFor: number) =>
      getMediaDetail({
        media_id: startedFor,
        navigation: navigationContextRef.current,
      }).then((resp) => {
        detailCacheRef.current.set(startedFor, resp);
        if (mediaIdRef.current === startedFor) applyDetail(resp);
      }),
    [applyDetail],
  );

  /** Surface a review-action error: toast 4xx, a 403 included since AKL-939
   * (the interceptor rejects those without toasting; 401/5xx/network it handles
   * itself). */
  const handleActionError = useCallback(
    (err: unknown) => {
      setActionBusy(false);
      const e = err as { status?: number; data?: { detail?: unknown } };
      if (typeof e?.status === "number" && e.status >= 400 && e.status < 500) {
        // The API returns a 422 detail either as a plain string or as a
        // structured `{error_code, message}` — the latter for the errors it wants
        // handled by code as well as read by a person. Surfacing `message` means
        // those cases name the actual reason (e.g. picking the blank species in
        // the assign-species flow) instead of falling back to the generic line.
        // The message is already translated server-side from the API's own
        // locale files, so it is approved copy, not raw server text.
        const raw = e.data?.detail as
          | string
          | { message?: unknown }
          | undefined;
        const detail =
          typeof raw === "string"
            ? raw
            : typeof raw?.message === "string"
              ? raw.message
              : "";
        toast.error(detail || t("Action failed. Please try again."));
      }
    },
    [t],
  );

  /**
   * Run a commit action, then refresh the surface from the detail endpoint.
   * Always calls refreshDetail for the acted-on photo, even if the reviewer
   * navigated away before the action resolved — refreshDetail's own guard
   * already limits UI-apply to the currently-open photo, so gating the call
   * itself here would (and previously did) leave that photo's cache entry
   * stale (AKL-707).
   */
  const runAction = useCallback(
    (fn: () => Promise<unknown>, action?: BusyAction) => {
      const startedFor = mediaIdRef.current;
      setBusyAction(action ?? null);
      setActionBusy(true);
      fn()
        .then(() => refreshDetail(startedFor))
        .catch((err) => {
          if (mediaIdRef.current !== startedFor) return;
          handleActionError(err);
        });
    },
    [refreshDetail, handleActionError],
  );

  const openAddObservation = () =>
    setEditor({
      context: "add",
      title: t("Add observation"),
      initial: {
        taxonId: null,
        label: null,
        individualCount: 1,
        comments: null,
      },
    });

  // --- AKL-698: card ↔ bbox highlight linkage ------------------------------

  /**
   * Pointer hover from a suggestion / observation card. `null` clears the
   * transient highlight; on leave we fall back to whatever card is
   * persistently selected (if any) so the highlight doesn't blink off.
   */
  const handleCardHover = useCallback(
    (cardKey: string | null, boxIds: string[]) => {
      if (cardKey == null) {
        // Mouse-leave: fall back to whatever should still be lit. In
        // assign-species mode that is the ticked set (AKL-774) - falling back to
        // the single-card selection instead would drop the reviewer's ticks off
        // the image the moment the pointer left a card. Otherwise it is the
        // persistently-selected card's highlight, or nothing.
        setHighlightedBoxIds(
          assignMode
            ? assignSelectedBoxIds(
                selectableSuggestions,
                selectedSuggestionKeys,
                boxes,
              )
            : selectedCardBoxIds,
        );
        return;
      }
      setHighlightedBoxIds(boxIds);
    },
    [
      selectedCardBoxIds,
      assignMode,
      boxes,
      selectedSuggestionKeys,
      selectableSuggestions,
    ],
  );

  /**
   * Pointer / keyboard activation on a card body — persists the selection
   * until another card is picked or the reviewer clears it (Escape, click
   * empty area, click same card again).
   */
  const handleCardClick = useCallback(
    (cardKey: string, boxIds: string[]) => {
      if (selectedCardKey === cardKey) {
        setSelectedCardKey(null);
        setSelectedCardBoxIds([]);
        setHighlightedBoxIds([]);
        setShowBoxLabels(true);
        return;
      }
      setSelectedCardKey(cardKey);
      setSelectedCardBoxIds(boxIds);
      setHighlightedBoxIds(boxIds);
      setShowBoxLabels(true);
    },
    [selectedCardKey],
  );

  const clearCardSelection = useCallback(() => {
    setSelectedCardKey(null);
    setSelectedCardBoxIds([]);
    setHighlightedBoxIds([]);
    setShowBoxLabels(true);
  }, []);

  // --- AKL-774: assign one species to several cards -------------------------

  /**
   * Tick or untick a card, and relight the image from the resulting set.
   *
   * The highlight is recomputed from the new set rather than added to
   * incrementally, so unticking removes exactly that card's boxes even when two
   * ticked cards happen to share one.
   */
  const toggleSuggestionSelected = useCallback(
    (cardKey: string) => {
      const next = new Set(selectedSuggestionKeys);
      if (next.has(cardKey)) next.delete(cardKey);
      else next.add(cardKey);
      setSelectedSuggestionKeys(next);
      setHighlightedBoxIds(
        assignSelectedBoxIds(selectableSuggestions, next, boxes),
      );
    },
    [boxes, selectableSuggestions, selectedSuggestionKeys],
  );

  /** Drop every tick but stay in the mode. */
  const clearSuggestionSelection = useCallback(() => {
    setSelectedSuggestionKeys(new Set());
    setHighlightedBoxIds([]);
  }, []);

  /**
   * Leave assign-species mode, dropping the ticks and the highlight they drove.
   * The AKL-698 single-card selection is cleared too: it was suppressed while
   * the mode was on, and restoring a highlight the reviewer last saw several
   * interactions ago would read as a stray selection.
   */
  const exitAssignMode = useCallback(() => {
    setAssignMode(false);
    setSelectedSuggestionKeys(new Set());
    // AKL-779: clear the ref too, not just the state. `applyDetail` reads this
    // ref to prune ticks that a refresh invalidated, and the ref is assigned
    // during render — so a detail refresh landing between this call and the next
    // render still saw the old ticks and re-lit their boxes, leaving the image
    // highlighted after the mode had closed. Every commit that exits the mode
    // refreshes the detail immediately afterwards, so that ordering is the
    // normal path rather than a rare one.
    selectedSuggestionKeysRef.current = new Set();
    setHighlightedBoxIds([]);
    setSelectedCardKey(null);
    setSelectedCardBoxIds([]);
    setShowBoxLabels(true);
  }, []);

  const toggleAssignMode = useCallback(() => {
    if (assignMode) {
      exitAssignMode();
      return;
    }
    // Entering clears the single-card highlight so the image starts blank and
    // fills in as cards are ticked.
    setAssignMode(true);
    setSelectedCardKey(null);
    setSelectedCardBoxIds([]);
    setHighlightedBoxIds([]);
    // Labels back on. The toggle that turns them off only renders while a single
    // card is selected, and entering this mode clears that selection — so a
    // reviewer who had switched labels off would otherwise be stuck with them
    // hidden and no control to bring them back.
    setShowBoxLabels(true);
  }, [assignMode, exitAssignMode]);

  /**
   * Open the observation editor for the ticked cards (AKL-774).
   *
   * The editor is the deliberate step - species picked, count checked, Save
   * pressed - which is why this flow has no separate confirmation dialog, unlike
   * the feed's bulk actions that commit straight from a bar.
   *
   * Opens with no species chosen (the reviewer is deciding it) and the count
   * prefilled from the number of BOXES, which is what the API would default to
   * if the request omitted it. The title counts CARDS. The help text under the
   * field exists because those two numbers differ.
   *
   * AKL-781: the picker's menu is seeded with the distinct taxa already on the
   * ticked cards, so the AI's own answer can be picked rather than retyped.
   * `taxonId` stays null even when that seed list has exactly one entry - the
   * offer is deliberately not a preselection (Margaux, 2026-09-02), because the
   * reviewer only opened this flow after judging the AI's grouping wrong, and a
   * preselected species would make Save a one-click commit of something they
   * never chose.
   */
  const openAssignSpecies = () => {
    if (assignSelected.cardCount === 0) return;
    setEditor({
      context: "assign-species",
      title: t("Assign species to {{count}} suggestions", {
        count: assignSelected.cardCount,
      }),
      initial: {
        taxonId: null,
        label: null,
        individualCount: assignSelected.boxCount,
        comments: null,
      },
      assignCards: assignSelected.payload,
      assignCardCount: assignSelected.cardCount,
      assignIdempotencyKey: newIdempotencyKey(),
      speciesOptions: assignSelected.speciesOptions,
      countHelp: t(
        "Prefilled from the number of boxes selected — change it if that is not the count.",
      ),
    });
  };

  /**
   * Commit the ticked cards, each at its OWN species (AKL-779).
   *
   * N ticked cards produce N observations - the opposite of Assign one species,
   * which merges them into one. No confirmation: nothing on this surface
   * confirms except Mark image blank, which does so because it destroys
   * existing observations.
   *
   * The card count is captured before the request because `exitAssignMode`
   * clears the selection and the toast needs the number. The toast and the exit
   * live INSIDE the promise chain so they run on success only - a failure must
   * leave the mode and the ticks intact, or a retry means re-ticking seven
   * cards. `runAction`'s catch handles that path.
   */
  const handleAcceptSelected = () => {
    if (assignSelected.cardCount === 0) return;
    const cardCount = assignSelected.cardCount;
    const idempotencyKey = newIdempotencyKey();
    runAction(() =>
      acceptSelectedCards(mediaId, assignSelected.payload, idempotencyKey).then(
        (resp) => {
          toast.success(
            t("{{count}} suggestions accepted.", { count: cardCount }),
          );
          exitAssignMode();
          return resp;
        },
      ),
    );
  };

  /**
   * Reject every ticked cluster (AKL-779). Creates no observations; a rejected
   * card stays recoverable through the existing re-action path. Same
   * success-only toast-and-exit contract as {@link handleAcceptSelected}.
   */
  const handleRejectSelected = () => {
    if (assignSelected.cardCount === 0) return;
    const cardCount = assignSelected.cardCount;
    const idempotencyKey = newIdempotencyKey();
    runAction(() =>
      rejectSelectedCards(mediaId, assignSelected.payload, idempotencyKey).then(
        (resp) => {
          toast.success(
            t("{{count}} suggestions rejected.", { count: cardCount }),
          );
          exitAssignMode();
          return resp;
        },
      ),
    );
  };

  const openEditSuggestion = (s: ResolvedSuggestion) =>
    setEditor({
      context: "aggregate-edit",
      title: t("Edit suggestion"),
      initial: {
        taxonId: s.target_taxon_id,
        label: s.display_name,
        individualCount: s.count,
        // Prefill the existing note (if this card already owns an observation) so
        // a taxon/count re-edit doesn't blank it — the editor always sends comments.
        comments: findExistingComment(detail, {
          mediaTaxonId: s.target_taxon_id,
        }),
      },
      predictionIds: s.underlying_prediction_ids,
      originalTaxonId: s.target_taxon_id,
    });

  /**
   * The species a box currently represents, and the aggregate whose count should
   * be decremented if this box leaves it — resolved from the box's owning item.
   *
   * AKL-703: for a box owned by a committed observation, the observation is the
   * authority on both. Reading them off the AI prediction instead is what broke
   * the per-box actions: AKL-700 excluded reviewed predictions from the resolve
   * pass, so an accepted box's `resolved_taxon_id` came back null (Accept and Edit
   * both bailed with an error toast) and `findParentTaxonId` found no suggestion
   * for it (so a box splitting out of an accepted aggregate never decremented the
   * parent — a silent double-count).
   *
   * The API now resolves reviewed predictions too, so the prediction path would no
   * longer return null — but the observation stays the authority, because it is the
   * more correct source: after an edit-on-accept the two taxa differ, and what the
   * reviewer committed is what the box shows.
   *
   * Only a media-scope owner has a count to decrement. An object-scope owner is
   * reconciled server-side by `source_prediction_id`, and passing its taxon as a
   * parent could wrongly decrement an unrelated media-level observation that
   * happens to share it.
   * @param box the box being actioned
   * @returns its current taxon (undefined when unresolved), display label, and
   *   the parent aggregate's taxon to decrement (undefined when there is none)
   */
  const boxActionContext = (box: DetailBox) => {
    const owner =
      box.ownerKind === "observation"
        ? ((detail?.observations ?? []).find(
            (o) => String(o.id) === box.ownerKey,
          ) ?? null)
        : null;
    if (owner) {
      return {
        taxonId: owner.taxon_id ?? undefined,
        label: owner.display_name ?? null,
        parentTaxonId:
          owner.applies_to === "media"
            ? (owner.taxon_id ?? undefined)
            : undefined,
      };
    }
    // Pending box: prefill from the *effective* prediction (the crop-
    // classification override when present) so it opens on the species the
    // reviewer sees on the box, not the generic detector label.
    const pred = predictionMap.get(box.sourcePredictionId) ?? null;
    const primary = pred ? pickPrimaryLabel(pred.labels) : null;
    return {
      taxonId: primary?.resolved_taxon_id ?? undefined,
      label: primary?.resolved_display_name ?? primary?.raw_label ?? null,
      // Both ids: for a crop-classified object the cluster holds the
      // label-bearing classification id, not the detection that owns the box.
      parentTaxonId: findParentTaxonId(
        detail,
        box.sourcePredictionId,
        box.predictionId,
      ),
    };
  };

  // The popover's "reviewed" chip: only set once a box has an actual review
  // decision — accepted/edited into an observation, or its suggestion is
  // tagged 'rejected'. A still-pending box has neither, so the raw AI
  // detection below is the only information there is to show.
  const selectedBoxReviewed =
    selectedBox && selectedBox.ownerKind === "observation"
      ? { rejected: false, label: boxActionContext(selectedBox).label ?? null }
      : selectedBox && selectedBox.visibility === "rejected"
        ? { rejected: true, label: null }
        : null;
  // AKL-717: an `unresolved` box — a detection whose only label the project
  // excluded. Its popover has no species to accept and offers Assign species
  // in place of Edit.
  const selectedBoxUnresolved = selectedBox?.visibility === "unresolved";

  const openEditObject = (box: DetailBox) => {
    // The review targets the box's own prediction id (which owns the geometry).
    const { taxonId, label, parentTaxonId } = boxActionContext(box);
    // The API edit requires `original_taxon_id`; block the edit when the box has
    // no resolved taxon (mirrors the accept guard) rather than sending an
    // incomplete request that would 422.
    if (taxonId == null) {
      toast.error(t("This detection has no resolved species to edit."));
      return;
    }
    setEditor({
      context: "object-edit",
      title: t("Edit detection"),
      initial: {
        taxonId,
        label,
        individualCount: 1,
        // Prefill the existing note (if this box already owns an object
        // observation) so a taxon re-edit doesn't blank it.
        comments: findExistingComment(detail, {
          sourcePredictionId: box.predictionId,
        }),
      },
      predictionIds: [box.predictionId],
      originalTaxonId: taxonId,
      parentTaxonId,
    });
  };

  /**
   * AKL-717: name a species for an `unresolved` box — a detection whose only
   * label the project excluded, so it has no species and no card. Opens the
   * editor with nothing preselected and nothing prefilled, deliberately NOT via
   * `boxActionContext`: its label fallback would surface the raw AI label, which
   * is exactly the excluded label this box must never show. Commits as an object
   * edit with a null original taxon (see `handleEditorConfirm`).
   * @param box the unresolved box being named
   */
  const openAssignSpeciesToBox = (box: DetailBox) =>
    setEditor({
      context: "object-assign",
      title: t("Assign species"),
      initial: {
        taxonId: null,
        label: null,
        individualCount: 1,
        comments: null,
      },
      predictionIds: [box.predictionId],
    });

  const openEditObservation = (obs: MediaDetailObservation) =>
    setEditor({
      context: "obs-edit",
      title: t("Edit observation"),
      initial: {
        taxonId: obs.taxon_id,
        label: obs.display_name,
        individualCount: obs.individual_count,
        comments: obs.comments,
      },
      observationId: obs.id,
    });

  const handleEditorConfirm = (fields: EditFields) => {
    if (!editor) return;
    switch (editor.context) {
      case "add":
        runAction(() => createObservation(mediaId, fields));
        break;
      case "aggregate-edit":
        runAction(() =>
          editSuggestion(
            mediaId,
            editor.predictionIds as number[],
            editor.originalTaxonId as number,
            "aggregate",
            fields,
            // The count the editor opened with (the card's displayed count), so
            // a taxon-only edit doesn't send new_count.
            editor.initial.individualCount,
          ),
        );
        break;
      case "object-edit":
        runAction(() =>
          editSuggestion(
            mediaId,
            editor.predictionIds as number[],
            editor.originalTaxonId as number,
            "object",
            fields,
            editor.initial.individualCount,
            editor.parentTaxonId,
          ),
        );
        break;
      case "object-assign":
        // AKL-717: no original taxon — the box had none. The API records the
        // change as "nothing → species" and creates the object observation; no
        // parent card exists to decrement, so none is named.
        runAction(() =>
          editSuggestion(
            mediaId,
            editor.predictionIds as number[],
            null,
            "object",
            fields,
            1,
          ),
        );
        break;
      case "obs-edit":
        runAction(() =>
          updateObservation(
            editor.observationId as number,
            buildObservationUpdateRequest(fields),
          ),
        );
        break;
      case "assign-species": {
        // One request carrying every ticked card, never one per card - see
        // `assignSpeciesToCards` for why that distinction is load-bearing.
        const cardCount = editor.assignCardCount ?? 0;
        runAction(() =>
          assignSpeciesToCards(
            mediaId,
            editor.assignCards as AssignSpeciesCardPayload[],
            fields,
            editor.assignIdempotencyKey as string,
          ).then((resp) => {
            toast.success(
              t("One observation created from {{count}} suggestions.", {
                count: cardCount,
              }),
            );
            // Only on success. A failure leaves the mode and the ticks intact
            // so a retry doesn't mean re-ticking seven cards.
            exitAssignMode();
            return resp;
          }),
        );
        break;
      }
    }
  };

  /**
   * Commit Mark as Blank, flip the reviewed marker (top-bar chip + filmstrip
   * thumbnail) once the commit succeeds, then advance to the next photo when
   * appropriate (§5.3.3).
   */
  const doMarkBlank = (triggeredBy: MarkBlankTrigger = "direct") => {
    const startedFor = mediaIdRef.current;
    const markedImageId = image.id;
    setConfirm(null);
    setActionBusy(true);
    markBlank(mediaId)
      .then(() => {
        // Mirrors doSave's success-only flip. `isReviewed` (top-bar chip) is
        // `optimisticallyReviewedImageIds.has(id) OR detail.review_status ===
        // "reviewed"`, so refreshDetail below is enough for the chip on its
        // own. But the filmstrip's greyed/checkmark treatment reads only this
        // set (not `detail`, which it never has for other photos) — without
        // adding here, the chip would still update but the thumbnail in the
        // strip would not.
        setOptimisticallyReviewedImageIds((prev) => {
          if (prev.has(markedImageId)) return prev;
          const next = new Set(prev);
          next.add(markedImageId);
          return next;
        });
        // Auto-advance only when the reviewer is still on this photo — it
        // would otherwise navigate relative to wherever they've since moved
        // to. refreshDetail always runs on every other path (including when
        // the reviewer navigated away) so startedFor's cache entry reflects
        // the mark-blank rather than staying stale (AKL-707).
        if (
          mediaIdRef.current === startedFor &&
          shouldAdvanceAfterMarkBlank(triggeredBy, canStepNext)
        ) {
          onStep(1);
          // Refresh (and re-cache, AKL-707) this photo's detail in the
          // background now that the mark-blank succeeded, so a later
          // re-visit doesn't serve stale pre-mark-blank data. Silent — the
          // reviewer has already moved on.
          refreshDetail(startedFor).catch(() => {});
          return;
        }
        // Advancing re-fetches the next photo via the media-change effect; on
        // the last photo (or when this triggered from plain Save), refresh in
        // place so the reviewed/blank state shows without navigating.
        return refreshDetail(startedFor);
      })
      .catch((err) => {
        if (mediaIdRef.current !== startedFor) return;
        handleActionError(err);
      });
  };

  const handleMarkBlankClick = () => {
    const pre = markBlankPreflight(detail);
    if (!pre.needsConfirm) {
      doMarkBlank("direct");
      return;
    }
    const message =
      pre.hasNonBlankObservations && pre.hasUnactionedPredictions
        ? t(
            "This photo already has observations and unreviewed AI predictions. This will reject the unreviewed predictions, remove the observations, and mark the photo as blank. Continue?",
          )
        : pre.hasUnactionedPredictions
          ? t(
              "This will reject all unreviewed AI predictions and mark the photo as blank. Continue?",
            )
          : t(
              "This photo already has observations. Marking it blank will remove them. Continue?",
            );
    setConfirm({
      kind: "mark-blank",
      message,
      confirmLabel: t("Mark image blank"),
      onConfirm: () => doMarkBlank("direct"),
    });
  };

  /**
   * Open the zero-observation prompt (§5.4/Q10) — the photo has nothing to
   * commit, so offer Mark as Blank or Cancel instead of a bare error.
   *
   * Shared by Save / Save & Next and by the bulk accept (AKL-752), which can
   * hit the same structured 422 when a concurrent reviewer already accepted
   * everything and the photo has no observations. One helper so the two paths
   * cannot drift on the wording or on what confirming does.
   * @param triggeredBy which control led here — decides whether confirming the
   *   Mark as Blank also advances to the next photo
   */
  const openZeroObservationConfirm = (triggeredBy: MarkBlankTrigger) =>
    setConfirm({
      kind: "zero-obs",
      message: t(
        "This image has no observations. If nothing is present, mark it blank so it counts as reviewed — otherwise cancel and add an observation.",
      ),
      confirmLabel: t("Mark as blank"),
      onConfirm: () => doMarkBlank(triggeredBy),
    });

  /**
   * Bulk-accept every pending suggestion on the current photo (AKL-752).
   *
   * One transactional request: the API derives the pending set from the same
   * aggregation these cards were rendered from, accepts them all, and marks the
   * photo reviewed. So the busy state is whole-photo (`accept-all`) rather than
   * a per-card spinner walking the list, there is exactly one detail refresh
   * rather than one per card, and a failure leaves the photo untouched instead
   * of half-accepted.
   *
   * Flips the reviewed marker on success only, mirroring `doSave` /
   * `doMarkBlank` — the filmstrip's greyed/checkmark treatment reads
   * `optimisticallyReviewedImageIds` alone (it has no `detail` for other
   * photos), so the refetch below updates the top-bar chip but not the
   * thumbnail without this. Deliberately does not auto-advance: Save & Next
   * stays the explicit "done, move on" control.
   */
  const handleAcceptAllPending = () => {
    if (visibleSuggestions.length === 0) return;
    const startedFor = mediaIdRef.current;
    const acceptedImageId = image.id;
    rememberCommitOpener();
    setBusyAction({ kind: "accept-all" });
    setActionBusy(true);
    acceptAllSuggestions(mediaId)
      .then(() => {
        setOptimisticallyReviewedImageIds((prev) => {
          if (prev.has(acceptedImageId)) return prev;
          const next = new Set(prev);
          next.add(acceptedImageId);
          return next;
        });
        // Always refreshes (and re-caches, AKL-707) startedFor's detail even if
        // the reviewer has navigated away — refreshDetail's own guard limits
        // the UI apply to the still-open photo. `actionBusy` is cleared there.
        return refreshDetail(startedFor);
      })
      .catch((err) => {
        if (mediaIdRef.current !== startedFor) return;
        // Only the zero-observation gate gets the Mark-as-Blank prompt — this
        // route can also 422 for an unknown / non-observable taxon, which must
        // surface as an error rather than a prompt that makes no sense here.
        // Reached when a concurrent reviewer already accepted everything and
        // the photo has no observations. Same prompt Save raises, no advance.
        if (isZeroObservationsError(err)) {
          setActionBusy(false);
          openZeroObservationConfirm("save");
          return;
        }
        handleActionError(err);
      });
  };

  /** Save (advance=false) or Save & Next (advance=true) (§5.4). */
  const doSave = (advance: boolean) => {
    const savedImageId = image.id;
    const savedMediaId = mediaId;

    // AKL-708: await the save before navigating (even for Save & Next) so a
    // zero-observation 422 always opens its dialog anchored to the photo
    // that actually failed, never one the reviewer has since moved past.
    const startedFor = mediaIdRef.current;
    rememberCommitOpener();
    setBusyAction({ kind: advance ? "save-next" : "save" });
    setActionBusy(true);
    saveMedia(savedMediaId, { suppressUnexpectedErrorRedirect: advance })
      .then(() => {
        // Only flip the "Reviewed" chip/badge once the save has actually
        // succeeded — never optimistically beforehand — so a
        // zero-observation 422 never flashes it on before the Mark-as-blank
        // dialog opens. (doMarkBlank mirrors this same success-only flip for
        // the Mark-as-blank path — see its own success handler below.)
        setOptimisticallyReviewedImageIds((prev) => {
          if (prev.has(savedImageId)) return prev;
          const next = new Set(prev);
          next.add(savedImageId);
          return next;
        });
        if (advance && mediaIdRef.current === startedFor && canStepNext) {
          onStep(1);
          // Refresh (and re-cache, AKL-707) this photo's detail in the
          // background now that the save succeeded, so a later re-visit
          // doesn't serve stale pre-save data. Silent — the reviewer has
          // already moved on.
          refreshDetail(startedFor).catch(() => {});
          return;
        }
        // Always refreshes (and re-caches, AKL-707) startedFor's detail, even
        // if the reviewer navigated away before this resolved — refreshDetail's
        // own guard already limits UI-apply to the currently-open photo. A
        // refreshDetail failure here is isolated with its own catch: the save
        // already succeeded, so it must not fall through to the catch below
        // and be treated as a save failure.
        return refreshDetail(startedFor).catch(() => {
          setActionBusy(false);
          toast.error(
            t("Photo saved, but could not refresh the display. Please reload."),
          );
        });
      })
      .catch((err) => {
        if (mediaIdRef.current !== startedFor) return;
        setActionBusy(false);
        // The interceptor handles 401 (token clear + redirect) and
        // network/timeout (toast); it rejects 4xx — a 403 included since
        // AKL-939 — without toasting. The
        // zero-observation gate (detail.error_code = "zero_observations",
        // §5.4/Q10) → prompt; other 4xx → surface the server detail if it is a
        // plain string. save() raises 422 only for that gate, but the check is
        // on the error_code rather than the bare status so an unexpected 422
        // shows its own error instead of a Mark-as-Blank prompt (AKL-752 made
        // this matter — accept-all shares the guard and has other 422s of its
        // own). For Save & Next
        // (advance=true), a 5xx also rejects locally here (AKL-706) with the
        // normalized {status, data} shape. Plain Save's 5xx still triggers the
        // interceptor's /unexpectedError redirect; the resulting TypeError can
        // still land in this catch, but it carries no `.status`, so none of
        // the branches below match for it.
        const e = err as { status?: number; data?: { detail?: unknown } };
        if (isZeroObservationsError(err)) {
          openZeroObservationConfirm(advance ? "save-next" : "save");
        } else if (
          typeof e?.status === "number" &&
          e.status >= 400 &&
          e.status < 500
        ) {
          const detail =
            typeof e.data?.detail === "string" ? e.data.detail : "";
          toast.error(
            detail || t("Could not save this photo. Please try again."),
          );
        } else if (typeof e?.status === "number" && e.status >= 500) {
          toast.error(t("Could not save this photo. Please try again."));
        }
      });
  };

  const handleDeleteObservation = (obs: MediaDetailObservation) =>
    setConfirm({
      kind: "delete-obs",
      message: t("Delete this observation? This cannot be undone."),
      confirmLabel: t("Delete"),
      onConfirm: () => runAction(() => deleteObservation(obs.id)),
    });

  /**
   * Navigation is deliberately NOT blocked while a commit is in flight
   * (AKL-752 R4). It used to be, on the grounds that a commit response "would
   * otherwise arrive after the photo changed" — but three guards already handle
   * exactly that, and blocking here was the blunt duplicate of them:
   *
   * - every commit callback drops its result when `mediaIdRef.current` no
   *   longer matches the photo the action started on;
   * - `refreshDetail` applies a response only to the still-open photo, while
   *   always refreshing that photo's cache entry (AKL-707) so a later re-visit
   *   sees the committed state;
   * - the media-change effect resets `actionBusy`, so a commit left in flight
   *   cannot strand the next photo in a disabled state.
   *
   * Controls that *commit* are still disabled while `actionBusy`; only closing
   * and moving between photos stay live.
   *
   * There is still no unsaved-editor prompt (§5.5), and none is needed: the
   * observation editor is a full-screen modal (z-[1200]) over the theater's
   * controls (z-50) and the keyboard handler bails while it is open, so
   * navigation is impossible until the editor is resolved — the modal enforces
   * "resolve before navigating", not `actionBusy`.
   */
  const handleStep = (direction: -1 | 1) => onStep(direction);
  const handleSelectIndex = (index: number) => onSelectIndex(index);
  const handleClose = () => onClose();

  const cardState = (s: ResolvedSuggestion): SuggestionReviewState =>
    deriveCardState(s, predictionMap);

  // --- Keyboard shortcuts (§6.2.6) -----------------------------------------
  // A single listener owns the review-surface keys while open. Guarded to
  // ignore keystrokes while typing in a field, and to let the editor / confirm
  // modals own their own keys. Kept behind a ref so it always sees fresh state
  // without re-subscribing (and without ManageReviewPage double-binding).
  const handleSurfaceKey = (e: KeyboardEvent) => {
    // A dialog layered above the theater owns the keyboard while it is open (AKL-798). The
    // tag-name guard below is not enough on its own: with the Report a problem dialog open,
    // focus on its drop-zone button or its panel div is neither an input nor a text area, so
    // `s` still reached `doSave` and committed the review — flipping the photo to Reviewed
    // behind a dialog the user cannot see past. `a`, `b`, `n`, Shift+S/Enter and the arrow
    // keys were the same.
    if (isTopLayerOpen()) return;
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName;
    // AKL-784 (review): a checkbox or radio accepts no typed text, so a
    // surface shortcut fired while one has focus cannot be swallowed keystrokes
    // — and clicking a suggestion card's tick box focuses it, so treating it
    // like a text field made `Escape` and `M` dead in exactly the state they
    // exist for: several cards ticked, reviewer wanting out. Space and Enter
    // still bail, because the control uses Space itself and `Enter` here is
    // Save & Next.
    const inputType =
      tag === "INPUT" ? target?.getAttribute("type")?.toLowerCase() : null;
    const isToggleInput = inputType === "checkbox" || inputType === "radio";
    if (
      (tag === "INPUT" && !isToggleInput) ||
      (isToggleInput && (e.key === " " || e.key === "Enter")) ||
      tag === "TEXTAREA" ||
      tag === "SELECT" ||
      target?.isContentEditable
    ) {
      return;
    }
    // Enter/Space already activate a focused button/link natively — don't also
    // fire a shortcut (which would e.g. Accept a card AND Save & Next at once).
    if (
      (e.key === "Enter" || e.key === " ") &&
      (tag === "BUTTON" ||
        tag === "A" ||
        target?.getAttribute("role") === "button")
    ) {
      return;
    }
    // The editor modal handles its own Escape; ignore all surface shortcuts
    // while it (or a confirmation) is open, except Escape to dismiss a confirm
    // and B to confirm the zero-observation dialog specifically (AKL-708).
    if (confirm) {
      if (e.key === "Escape") setConfirm(null);
      else if (
        !e.repeat &&
        !actionBusy &&
        isMarkBlankConfirmHotkey(confirm.kind, e.key)
      )
        confirm.onConfirm();
      return;
    }
    if (editor) return;
    if (showHelp) {
      if (e.key === "Escape" || e.key === "?" || e.key === "h" || e.key === "H")
        setShowHelp(false);
      return;
    }
    // While the brightness popover is open, Escape closes the popover instead
    // of the surface, and no other global shortcut fires so the reviewer can
    // finish adjusting without side effects.
    if (brightnessPopoverOpen) {
      if (e.key === "Escape") setBrightnessPopoverOpen(false);
      return;
    }
    // Ignore auto-repeat (holding a key) unconditionally, so a held/rapid key
    // cannot fire an action twice — each commit mints its own idempotency key,
    // so the dedupe has to happen here.
    if (e.repeat) return;
    // While a commit is in flight, only the navigation keys get through
    // (AKL-752 R4); every key that commits something stays blocked.
    if (actionBusy && !isAllowedWhileCommitting(e.key)) return;

    switch (e.key) {
      case "ArrowRight":
        if (canStepNext) handleStep(1);
        break;
      case "ArrowLeft":
        if (canStepPrev) handleStep(-1);
        break;
      case "s":
      case "S":
        // Prevent the browser default for any Ctrl/Cmd combo (incl.
        // Ctrl/Cmd+Shift+S "Save As…") before handling Save / Save & Next.
        if (e.ctrlKey || e.metaKey) e.preventDefault();
        if (e.shiftKey) {
          doSave(true);
        } else {
          doSave(false);
        }
        break;
      case "Enter":
        doSave(true);
        break;
      case "b":
      case "B":
        // AKL-784 (review): off in select-suggestions mode for the same reason
        // as accept-all - marking the photo blank rejects every unreviewed
        // prediction, which is the ticked set.
        if (!assignMode) handleMarkBlankClick();
        break;
      case "n":
      case "N":
        // AKL-698: `N` opens the Add-observation editor (photo navigation
        // is now arrow-keys-only).
        openAddObservation();
        break;
      case "m":
      case "M":
        // AKL-781: keyboard equivalent of the Select suggestions button, which
        // was the only action on this surface without one. `S` is Save
        // (Shift+S Save & Next), so `M` — unbound until now — takes it.
        //
        // Calls the same `toggleAssignMode` the button does, so it toggles both
        // ways and leaving clears the ticks and the highlight, and it is gated
        // on the same expression the button's render is.
        //
        // The guard is "can toggle", not "can enter" (review of AKL-781):
        // `canEnterAssignMode` can go false *while the mode is on* — accept-all
        // consumes every pending card — and gating the toggle on it alone left
        // the mode with neither its button nor its hotkey, which is the very
        // trap the guard exists to prevent.
        //
        // No editor / confirmation / help / brightness / in-flight guard here
        // on purpose: `handleSurfaceKey` returns before this switch in every
        // one of those states, and `M` is not in `isAllowedWhileCommitting`. A
        // second guard would read as though those early returns weren't
        // trusted.
        if (canToggleAssignMode) toggleAssignMode();
        break;
      case "a":
      case "A":
        // AKL-698: `A` is dual-purpose. When a suggestion card has keyboard
        // focus, accept THAT card (existing behavior). Otherwise, invoke the
        // ACTIONS-panel bulk Accept observations.
        //
        // AKL-774: the card-focused half is off while assign-species mode is on.
        // `cardFocusIndex` is set by focus on any control inside a card, and in
        // that mode that includes the tick checkbox - so tabbing to a checkbox
        // and pressing A would silently accept that one card, bypassing the
        // selection the reviewer is building. The mode already hides the
        // per-card buttons; this is the keyboard equivalent of the same promise.
        //
        // AKL-779: inside the mode `A` means "accept the ticked selection". The
        // key is free to take on that meaning because both branches below are
        // already switched off in the mode - the card-focused one by AKL-774,
        // accept-all by AKL-784 - so nothing is being taken away. Checked first
        // so the mode's meaning always wins, including when a tick checkbox has
        // focus (which sets cardFocusIndex).
        if (assignMode) {
          handleAcceptSelected();
        } else if (
          cardFocusIndex != null &&
          visibleSuggestions[cardFocusIndex]
        ) {
          const s = visibleSuggestions[cardFocusIndex];
          runAction(
            () =>
              acceptSuggestion(
                mediaId,
                s.underlying_prediction_ids,
                s.target_taxon_id,
                "aggregate",
              ),
            { kind: "suggestion-accept", key: suggestionOwnerKey(s) },
          );
        } else if (visibleSuggestions.length > 0) {
          // AKL-784 (review): accept-all is off in select-suggestions mode.
          // It consumes every pending card, so one keystroke silently threw
          // away the selection the reviewer was building - and then emptied
          // the mode of anything to tick. The card-focused half above is
          // already off in the mode; this closes the other half.
          handleAcceptAllPending();
        }
        break;
      case "r":
      case "R":
        // AKL-779: inside the mode `R` rejects the ticked selection, mirroring
        // `A`. Outside it, the card-focused reject is unchanged.
        if (assignMode) {
          handleRejectSelected();
        } else if (
          cardFocusIndex != null &&
          visibleSuggestions[cardFocusIndex]
        ) {
          const s = visibleSuggestions[cardFocusIndex];
          runAction(
            () =>
              rejectSuggestion(
                mediaId,
                s.underlying_prediction_ids,
                "aggregate",
                s.target_taxon_id,
              ),
            { kind: "suggestion-reject", key: suggestionOwnerKey(s) },
          );
        }
        break;
      case "e":
      case "E":
        // Card-focused edit: off in assign-species mode, same as `A` and `R`.
        if (
          !assignMode &&
          cardFocusIndex != null &&
          visibleSuggestions[cardFocusIndex]
        )
          openEditSuggestion(visibleSuggestions[cardFocusIndex]);
        break;
      case " ":
        e.preventDefault();
        setShowBoxes((v) => !v);
        break;
      case "+":
      case "=":
        canvasRef.current?.zoomIn();
        break;
      case "-":
      case "_":
        canvasRef.current?.zoomOut();
        break;
      case "?":
      case "h":
      case "H":
        setShowHelp(true);
        break;
      case "Escape":
        if (selectedBoxId || popoverAnchor) {
          setSelectedBoxId(null);
          setPopoverAnchor(null);
        } else if (assignMode) {
          // AKL-784: leaving select-suggestions mode, NOT closing the surface.
          // Entering the mode nulls `selectedCardKey` (the highlight belongs to
          // the ticked set, not to one focused card), so without this branch
          // Escape fell through to `handleClose()` — a reviewer who had ticked
          // seven cards and pressed Escape to cancel lost the ticks and the
          // photo. Sits after the popover check so a box popover opened during
          // the mode is still the first thing Escape dismisses.
          exitAssignMode();
        } else if (selectedCardKey != null) {
          // AKL-698: Escape clears the card ↔ bbox linkage selection before
          // it closes the surface, so the reviewer can dismiss a highlight
          // without leaving the photo.
          clearCardSelection();
        } else {
          handleClose();
        }
        break;
      default:
        break;
    }
  };

  const keyHandlerRef = useRef(handleSurfaceKey);
  keyHandlerRef.current = handleSurfaceKey;

  useEffect(() => {
    const listener = (e: KeyboardEvent) => keyHandlerRef.current(e);
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, []);

  // Close the brightness popover when a mousedown lands outside its control
  // wrapper. The wrapper ref (`brightnessControlRef`) covers BOTH the trigger
  // button and the popover, so `contains(target)` treats a click on either as
  // an inside click — otherwise the outside-click listener would fight the
  // toggle button (mousedown-outside would close the popover, and the ensuing
  // click on the button would reopen it). Bound only while the popover is
  // open so the rest of the surface pays no cost.
  useEffect(() => {
    if (!brightnessPopoverOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (
        brightnessControlRef.current &&
        target &&
        !brightnessControlRef.current.contains(target)
      ) {
        setBrightnessPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [brightnessPopoverOpen]);

  const sectionHeading =
    "text-xs font-semibold uppercase tracking-wide text-off-black-light/70 dark:text-primary-off-white/60";
  const toolbarBtn =
    "flex h-9 w-9 items-center justify-center rounded-md bg-black/5 text-lg leading-none text-off-black-light hover:bg-black/15 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none transition-colors dark:bg-white/5 dark:text-primary-off-white dark:hover:bg-white/15";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={() => handleClose()}
    >
      <div
        className="relative flex h-[92%] w-[96%] flex-col overflow-hidden rounded-lg bg-primary-off-white text-off-black-light shadow-2xl dark:bg-storm-blue-dark dark:text-primary-off-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top info bar */}
        <div className="flex items-center justify-between gap-3 border-b border-black/10 px-4 py-2 dark:border-white/10">
          <div className="flex min-w-0 items-center gap-3">
            <span className="truncate font-semibold" title={displayName}>
              {displayName}
            </span>
            {captureText && (
              <span className="shrink-0 text-xs text-off-black-light/70 dark:text-primary-off-white/50">
                {captureText}
              </span>
            )}
            {(deploymentName || locationName) && (
              <span className="shrink-0 rounded bg-black/10 px-2 py-0.5 text-xs text-off-black-light dark:bg-white/10 dark:text-primary-off-white/80">
                {[deploymentName, locationName].filter(Boolean).join(" · ")}
              </span>
            )}
            {isReviewed && (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-green-600 px-2 py-0.5 text-xs font-medium text-white"
                data-testid="reviewed-chip"
              >
                <svg
                  width={12}
                  height={12}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                {t("Reviewed")}
              </span>
            )}
            <Tooltip title={t(titleKey)}>
              <button
                type="button"
                className="shrink-0 rounded p-1 focus-visible:ring-2 focus-visible:ring-storm-blue focus:outline-none dark:focus-visible:ring-white"
                onClick={(e) => {
                  e.stopPropagation();
                  // navigator.clipboard is undefined in non-secure contexts /
                  // some browsers; guard so the click never throws synchronously.
                  if (!navigator.clipboard) {
                    setTitleKey("Could not copy link");
                    return;
                  }
                  navigator.clipboard
                    .writeText(getImageRoute(image))
                    .then(() => setTitleKey("Link copied to clipboard!"))
                    .catch(() => setTitleKey("Could not copy link"));
                }}
                aria-label={t("Copy link")}
              >
                <LinkIcon className="text-storm-blue transition duration-300 dark:text-primary-off-white" />
              </button>
            </Tooltip>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {selectedCardKey != null && (
              <label
                className="flex items-center gap-1.5 text-xs text-off-black-light/80 dark:text-primary-off-white/70"
                data-testid="show-labels-toggle"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 cursor-pointer accent-sky-blue-dark"
                  checked={showBoxLabels}
                  onChange={(e) => setShowBoxLabels(e.target.checked)}
                />
                {t("Show labels + confidence")}
              </label>
            )}
            <span className="text-xs text-off-black-light/70 dark:text-primary-off-white/50">
              {t("zoom {{percent}}%", { percent: zoomPercent })}
            </span>
            <button
              type="button"
              className="rounded-full bg-black/10 p-1.5 text-off-black-light hover:bg-black/20 focus-visible:ring-2 focus-visible:ring-storm-blue focus:outline-none dark:bg-white/10 dark:text-white dark:hover:bg-white/20 dark:focus-visible:ring-white"
              onClick={() => handleClose()}
              aria-label={t("Close")}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Main row: tool rail + image + review panel */}
        <div className="flex min-h-0 flex-1">
          {/* Left tool rail — a separate panel, not overlaid on the image */}
          <div className="flex shrink-0 flex-col gap-1.5 border-r border-black/10 bg-off-white-dark p-2 dark:border-white/10 dark:bg-storm-blue">
            <button
              type="button"
              className={toolbarBtn}
              onClick={() => canvasRef.current?.zoomIn()}
              aria-label={t("Zoom in")}
              title={t("Zoom in")}
              data-testid="canvas-zoom-in"
            >
              +
            </button>
            <button
              type="button"
              className={toolbarBtn}
              onClick={() => canvasRef.current?.zoomOut()}
              aria-label={t("Zoom out")}
              title={t("Zoom out")}
              data-testid="canvas-zoom-out"
            >
              −
            </button>
            <button
              type="button"
              className={toolbarBtn}
              onClick={() => canvasRef.current?.resetView()}
              aria-label={t("Reset zoom")}
              title={t("Reset zoom")}
              data-testid="canvas-zoom-reset"
            >
              ⟲
            </button>
            <button
              type="button"
              className={`${toolbarBtn} ${
                magnifierOn
                  ? "!bg-sky-blue-light/60 dark:!bg-sky-blue-light/30"
                  : ""
              }`}
              onClick={() => setMagnifierOn((v) => !v)}
              aria-pressed={magnifierOn}
              aria-label={t("Toggle magnifier")}
              title={t("Toggle magnifier")}
              data-testid="canvas-magnifier-toggle"
            >
              🔍
            </button>
            <button
              type="button"
              className={`${toolbarBtn} ${
                showBoxes
                  ? "!bg-sky-blue-light/60 dark:!bg-sky-blue-light/30"
                  : ""
              }`}
              onClick={() => {
                setShowBoxes((v) => !v);
                // Hiding boxes closes any open per-box popover/selection.
                setSelectedBoxId(null);
                setPopoverAnchor(null);
              }}
              aria-pressed={showBoxes}
              aria-label={t("Toggle bounding boxes")}
              title={t("Toggle bounding boxes")}
              data-testid="canvas-boxes-toggle"
            >
              ▢
            </button>
            <div ref={brightnessControlRef} className="relative">
              <button
                type="button"
                className={`${toolbarBtn} ${
                  brightness !== DEFAULT_BRIGHTNESS
                    ? "!bg-sky-blue-light/60 dark:!bg-sky-blue-light/30"
                    : ""
                }`}
                onClick={() => setBrightnessPopoverOpen((v) => !v)}
                aria-pressed={brightness !== DEFAULT_BRIGHTNESS}
                aria-haspopup="dialog"
                aria-expanded={brightnessPopoverOpen}
                aria-label={t("Brightness")}
                title={t("Brightness")}
                data-testid="canvas-brightness-toggle"
              >
                <BrightMode />
              </button>
              {brightnessPopoverOpen && (
                <div
                  className="absolute left-full top-0 ml-2 z-30 w-56 rounded border border-black/10 bg-primary-off-white p-3 text-off-black-light shadow-lg dark:border-white/10 dark:bg-storm-blue dark:text-primary-off-white"
                  role="dialog"
                  aria-label={t("Brightness")}
                  data-testid="canvas-brightness-popover"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wide">
                    <label htmlFor="canvas-brightness-slider">
                      {t("Brightness")}
                    </label>
                    <span
                      className="text-off-black-light/70 dark:text-primary-off-white/70"
                      data-testid="canvas-brightness-value"
                    >
                      {t("{{percent}}%", {
                        percent: Math.round(brightness * 100),
                      })}
                    </span>
                  </div>
                  <input
                    id="canvas-brightness-slider"
                    type="range"
                    min={Math.round(MIN_BRIGHTNESS * 100)}
                    max={Math.round(MAX_BRIGHTNESS * 100)}
                    step={1}
                    value={Math.round(brightness * 100)}
                    onChange={(e) =>
                      setBrightness(Number(e.target.value) / 100)
                    }
                    onKeyDown={(e) => {
                      // The global keydown handler returns early for INPUT
                      // targets, so Escape needs a local handler here to close
                      // the popover while the slider has focus. stopPropagation
                      // prevents the outer Escape (which would close the whole
                      // review surface) from also firing.
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        setBrightnessPopoverOpen(false);
                      }
                    }}
                    className="w-full accent-sky-blue-light"
                    data-testid="canvas-brightness-slider"
                  />
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      className="rounded px-2 py-1 text-xs font-semibold text-off-black-light hover:bg-black/10 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none dark:text-primary-off-white dark:hover:bg-white/10"
                      onClick={() => setBrightness(DEFAULT_BRIGHTNESS)}
                      data-testid="canvas-brightness-reset"
                    >
                      {t("Reset")}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              className={`${toolbarBtn} ${
                sharpen
                  ? "!bg-sky-blue-light/60 dark:!bg-sky-blue-light/30"
                  : ""
              }`}
              onClick={() => setSharpen((v) => !v)}
              aria-pressed={sharpen}
              aria-label={t("Sharpen")}
              title={t("Sharpen")}
              data-testid="canvas-sharpen-toggle"
            >
              ✧
            </button>
          </div>

          <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-gray-200 dark:bg-black/40">
            <ImageCanvas
              ref={canvasRef}
              imageUrl={image.url}
              imageAlt={displayName}
              boxes={showBoxes ? boxes : []}
              frameLabels={showBoxes ? (image.frameLabels ?? []) : []}
              selectedBoxId={selectedBoxId}
              highlightedBoxIds={highlightedBoxIds}
              showLabels={showBoxLabels}
              onBoxClick={handleBoxClick}
              onZoomChange={setZoomPercent}
              magnifierOn={magnifierOn}
              imageFilter={imageFilter}
            />
            {/* Prev/next navigation over the feed working set. */}
            <button
              type="button"
              className="absolute left-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/80 disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-white focus:outline-none"
              onClick={() => handleStep(-1)}
              disabled={!canStepPrev}
              aria-label={t("Previous photo")}
              data-testid="fullscreen-prev"
            >
              <PrevArrow />
            </button>
            <button
              type="button"
              className="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/50 p-2 text-white hover:bg-black/80 disabled:opacity-30 focus-visible:ring-2 focus-visible:ring-white focus:outline-none"
              onClick={() => handleStep(1)}
              disabled={!canStepNext}
              aria-label={t("Next photo")}
              data-testid="fullscreen-next"
            >
              <NextArrow />
            </button>
          </div>

          {/* Review panel */}
          <div className="flex w-[400px] shrink-0 flex-col overflow-y-auto border-l border-black/10 bg-off-white-dark dark:border-white/10 dark:bg-storm-blue">
            <div className="border-b border-black/10 p-4 dark:border-white/10">
              <h2 className="font-semibold">{t("Review panel")}</h2>
              <p className="mt-0.5 text-xs text-off-black-light/70 dark:text-primary-off-white/50">
                {t("{{detections}} AI detections · {{suggested}} suggested", {
                  detections: detectionCount,
                  suggested: visibleSuggestions.length,
                })}
              </p>
            </div>

            <div className="flex-1 space-y-5 p-4">
              {loading && (
                <div className="flex justify-center py-6">
                  <BeatLoader
                    color={spinnerColor}
                    loading={loading}
                    size={12}
                  />
                </div>
              )}

              {error && !loading && (
                <p
                  className="text-sm text-red-600 dark:text-red-400"
                  data-testid="fullscreen-error"
                >
                  {t("Could not load photo details. Please try again.")}
                </p>
              )}

              {detail && !loading && (
                <>
                  <section>
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <h3 className={sectionHeading}>
                        {t("Suggested observations ({{count}})", {
                          count: visibleSuggestions.length,
                        })}
                      </h3>
                      {canToggleAssignMode && (
                        <button
                          type="button"
                          className={`inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs font-semibold focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none disabled:opacity-40 ${
                            assignMode
                              ? "bg-sky-blue text-storm-blue"
                              : "text-sky-blue-dark hover:underline dark:text-sky-blue"
                          }`}
                          onClick={toggleAssignMode}
                          disabled={actionBusy}
                          aria-pressed={assignMode}
                          aria-keyshortcuts="m"
                          data-testid="assign-species-toggle"
                        >
                          {t("Select suggestions")}
                          {/* AKL-785: Show the M hotkey as a visual hint. Keep it
                              aria-hidden so the accessible name stays "Select suggestions";
                              aria-keyshortcuts above exposes the shortcut to assistive tech.
                              Use an outline chip (border-current) because the toggle is
                              link-styled when inactive. */}
                          <kbd
                            aria-hidden="true"
                            className="shrink-0 rounded border border-current px-1.5 py-0.5 font-mono text-[10px] leading-none"
                          >
                            M
                          </kbd>
                        </button>
                      )}
                    </div>
                    {assignMode && (
                      <div
                        className="mb-2 flex flex-wrap items-center gap-3 rounded bg-sky-blue/10 px-3 py-2"
                        data-testid="assign-species-bar"
                      >
                        <span className="text-xs font-semibold text-storm-blue dark:text-sky-blue-light">
                          {t("{{count}} suggestions selected", {
                            count: assignSelected.cardCount,
                          })}
                        </span>
                        <button
                          type="button"
                          className="text-xs font-semibold text-sky-blue-dark hover:underline disabled:opacity-40 dark:text-sky-blue"
                          onClick={openAssignSpecies}
                          disabled={
                            actionBusy || assignSelected.cardCount === 0
                          }
                          data-testid="assign-species-confirm"
                        >
                          {t("Assign one species")}
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-blue-dark hover:underline disabled:opacity-40 dark:text-sky-blue"
                          onClick={handleAcceptSelected}
                          disabled={
                            actionBusy || assignSelected.cardCount === 0
                          }
                          // The chip is aria-hidden, so this is what actually
                          // exposes the shortcut to assistive tech.
                          aria-keyshortcuts="a"
                          data-testid="assign-species-accept"
                        >
                          {t("Accept as suggested")}
                          {/* Outline chip, matching the Select suggestions
                              toggle rather than the ACTIONS panel: these buttons
                              are link-styled, so a filled chip has no surface to
                              sit on. */}
                          <kbd
                            aria-hidden="true"
                            className="shrink-0 rounded border border-current px-1.5 py-0.5 font-mono text-[10px] leading-none"
                          >
                            A
                          </kbd>
                        </button>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:underline disabled:opacity-40 dark:text-red-400"
                          onClick={handleRejectSelected}
                          disabled={
                            actionBusy || assignSelected.cardCount === 0
                          }
                          aria-keyshortcuts="r"
                          data-testid="assign-species-reject"
                        >
                          {t("Reject selected")}
                          <kbd
                            aria-hidden="true"
                            className="shrink-0 rounded border border-current px-1.5 py-0.5 font-mono text-[10px] leading-none"
                          >
                            R
                          </kbd>
                        </button>
                        <button
                          type="button"
                          className="ml-auto text-[11px] text-off-black-light/70 hover:underline disabled:opacity-40 dark:text-primary-off-white/50"
                          onClick={clearSuggestionSelection}
                          disabled={
                            actionBusy || assignSelected.cardCount === 0
                          }
                          data-testid="assign-species-clear"
                        >
                          {t("Clear selection")}
                        </button>
                      </div>
                    )}
                    {visibleSuggestions.length > 0 ? (
                      <div className="space-y-2">
                        {visibleSuggestions.map((s, i) => {
                          const boxIds = boxIdsForOwner(
                            boxes,
                            suggestionOwnerKey(s),
                          );
                          return (
                            <SuggestionCard
                              key={suggestionOwnerKey(s)}
                              suggestion={s}
                              reviewState={cardState(s)}
                              focused={cardFocusIndex === i}
                              onFocusCard={() => setCardFocusIndex(i)}
                              onBlurCard={() => setCardFocusIndex(null)}
                              busy={actionBusy}
                              pending={suggestionPending(suggestionOwnerKey(s))}
                              onAccept={() =>
                                runAction(
                                  () =>
                                    acceptSuggestion(
                                      mediaId,
                                      s.underlying_prediction_ids,
                                      s.target_taxon_id,
                                      "aggregate",
                                    ),
                                  {
                                    kind: "suggestion-accept",
                                    key: suggestionOwnerKey(s),
                                  },
                                )
                              }
                              onReject={() =>
                                runAction(
                                  () =>
                                    rejectSuggestion(
                                      mediaId,
                                      s.underlying_prediction_ids,
                                      "aggregate",
                                      s.target_taxon_id,
                                    ),
                                  {
                                    kind: "suggestion-reject",
                                    key: suggestionOwnerKey(s),
                                  },
                                )
                              }
                              onEdit={() => openEditSuggestion(s)}
                              selected={
                                selectedCardKey === suggestionOwnerKey(s)
                              }
                              onCardHover={(k) => handleCardHover(k, boxIds)}
                              onCardClick={(k) => handleCardClick(k, boxIds)}
                              selectable={assignMode}
                              checked={selectedSuggestionKeys.has(
                                suggestionOwnerKey(s),
                              )}
                              onToggleSelect={toggleSuggestionSelected}
                            />
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-off-black-light/70 dark:text-primary-off-white/50">
                        {t("No AI suggestions for this photo.")}
                      </p>
                    )}
                  </section>

                  {rejectedSuggestions.length > 0 && (
                    <section data-testid="rejected-predictions-section">
                      <h3 className={`${sectionHeading} mb-2`}>
                        {t("Rejected predictions ({{count}})", {
                          count: rejectedSuggestions.length,
                        })}
                      </h3>
                      <div className="space-y-2">
                        {rejectedSuggestions.map((s) => {
                          const boxIds = boxIdsForOwner(
                            boxes,
                            suggestionOwnerKey(s),
                          );
                          return (
                            <SuggestionCard
                              key={suggestionOwnerKey(s)}
                              suggestion={s}
                              reviewState="rejected"
                              busy={actionBusy}
                              pending={suggestionPending(suggestionOwnerKey(s))}
                              onAccept={() =>
                                runAction(
                                  () =>
                                    acceptSuggestion(
                                      mediaId,
                                      s.underlying_prediction_ids,
                                      s.target_taxon_id,
                                      "aggregate",
                                    ),
                                  {
                                    kind: "suggestion-accept",
                                    key: suggestionOwnerKey(s),
                                  },
                                )
                              }
                              onEdit={() => openEditSuggestion(s)}
                              selected={
                                selectedCardKey === suggestionOwnerKey(s)
                              }
                              onCardHover={(k) => handleCardHover(k, boxIds)}
                              onCardClick={(k) => handleCardClick(k, boxIds)}
                              // Rejected cards are tickable alongside pending
                              // ones in the same selection (AKL-774): re-pointing
                              // a rejected cluster is a re-action the API already
                              // handles, so there is nothing to keep apart.
                              selectable={assignMode}
                              checked={selectedSuggestionKeys.has(
                                suggestionOwnerKey(s),
                              )}
                              onToggleSelect={toggleSuggestionSelected}
                            />
                          );
                        })}
                      </div>
                    </section>
                  )}

                  <section data-testid="actions-section">
                    <h3 className={`${sectionHeading} mb-2`}>{t("Actions")}</h3>
                    <div className="space-y-2">
                      <button
                        type="button"
                        className={`relative flex w-full items-center justify-center gap-2 rounded bg-green-600/90 px-3 py-2 text-sm font-semibold text-white hover:bg-green-600 focus-visible:ring-2 focus-visible:ring-green-400 focus:outline-none ${
                          busyAction?.kind === "accept-all"
                            ? ""
                            : "disabled:opacity-40"
                        }`}
                        onClick={handleAcceptAllPending}
                        // AKL-784 (review): disabled in select-suggestions
                        // mode. Committing every pending card here would
                        // discard the selection being built and leave the mode
                        // with nothing to tick. Greyed out with no explanatory
                        // tooltip on purpose - the button already greys out
                        // silently when there is nothing to accept, and new
                        // copy needs sign-off.
                        disabled={
                          actionBusy ||
                          assignMode ||
                          visibleSuggestions.length === 0
                        }
                        // Dropped in select-suggestions mode: `A` is
                        // repurposed there to accept the ticked selection, so
                        // announcing it on this (disabled) button would point
                        // assistive tech at a key that activates something else.
                        aria-keyshortcuts={assignMode ? undefined : "a"}
                        // The label below is hidden with `invisible`
                        // (visibility: hidden) while committing so the button
                        // does not resize - which also drops it from the
                        // accessible name, leaving the control nameless
                        // mid-commit. The ✓ and the `A` chip are both
                        // aria-hidden (AKL-779 AC16), so nothing else names it.
                        // An explicit aria-label wins over the emptied content
                        // and keeps the name stable in both states. Applied
                        // unconditionally rather than only while busy: the
                        // value is byte-identical to the visible text, so it
                        // changes nothing when idle, and a name that comes and
                        // goes is worse than a constant one.
                        aria-label={t("Accept observations")}
                        // Gated on `actionBusy` as well as the kind, unlike the
                        // spinner below. `busyAction` is cleared only by the
                        // effect keyed on `actionBusy`, so between a commit
                        // settling (`setActionBusy(false)`) and that effect
                        // running there is one render where the kind still reads
                        // "accept-all". A stale spinner for one render is
                        // invisible; a stale aria-busy is a claim about state,
                        // and `actionBusy` is the flag that actually means
                        // "in flight" (Copilot, PR #788).
                        aria-busy={
                          actionBusy && busyAction?.kind === "accept-all"
                        }
                        data-testid="accept-observations"
                      >
                        {/* AKL-752: one whole-photo commit, so this button owns
                            the spinner (no per-card cascade). Label and kbd hint
                            stay in layout while hidden so the button doesn't
                            resize, matching the Save button below. */}
                        <span
                          className={
                            busyAction?.kind === "accept-all"
                              ? "invisible"
                              : undefined
                          }
                        >
                          <span aria-hidden="true">✓ </span>
                          {t("Accept observations")}
                        </span>
                        {/* aria-hidden, like every other chip on the surface:
                            a decorative hint, never part of the accessible name
                            (the QA Swarm failed AKL-779 AC16 on this button
                            being read as "✓ Accept observations A"). */}
                        <kbd
                          aria-hidden="true"
                          className={`rounded bg-black/20 px-1.5 py-0.5 text-[10px] font-mono ${
                            busyAction?.kind === "accept-all" ? "invisible" : ""
                          }`}
                        >
                          A
                        </kbd>
                        {busyAction?.kind === "accept-all" && (
                          <span className="absolute inset-0 flex items-center justify-center">
                            <BeatLoader
                              color="#ffffff"
                              loading={true}
                              size={8}
                              margin={3}
                            />
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center justify-center gap-2 rounded border border-red-500/60 bg-transparent px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-500/10 focus-visible:ring-2 focus-visible:ring-red-400 focus:outline-none disabled:opacity-40 dark:text-red-400"
                        onClick={handleMarkBlankClick}
                        // AKL-784 (review): disabled in select-suggestions
                        // mode - marking blank rejects every unreviewed
                        // prediction, i.e. the ticked set.
                        disabled={actionBusy || assignMode}
                        // Same as above: `B` is withheld in the mode
                        // (AKL-784), so the shortcut is not true there.
                        aria-keyshortcuts={assignMode ? undefined : "b"}
                        data-testid="mark-blank"
                      >
                        <span>
                          <span aria-hidden="true">⊘ </span>
                          {t("Mark image blank")}
                        </span>
                        <kbd
                          aria-hidden="true"
                          className="rounded bg-black/20 px-1.5 py-0.5 text-[10px] font-mono"
                        >
                          B
                        </kbd>
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center justify-center gap-2 rounded border border-black/20 bg-transparent px-3 py-2 text-sm font-semibold text-off-black-light hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none disabled:opacity-40 dark:border-white/20 dark:text-primary-off-white dark:hover:bg-white/5"
                        onClick={openAddObservation}
                        disabled={actionBusy}
                        aria-keyshortcuts="n"
                        data-testid="add-observation"
                      >
                        <span>
                          <span aria-hidden="true">+ </span>
                          {t("Add observation")}
                        </span>
                        <kbd
                          aria-hidden="true"
                          className="rounded bg-black/20 px-1.5 py-0.5 text-[10px] font-mono"
                        >
                          N
                        </kbd>
                      </button>
                    </div>
                  </section>

                  <section>
                    <h3 className={`${sectionHeading} mb-2`}>
                      {t("Observations ({{count}})", {
                        count: (detail.observations ?? []).filter(
                          (o) => o.status !== "deleted",
                        ).length,
                      })}
                    </h3>
                    <ObservationList
                      observations={detail.observations}
                      boxes={boxes}
                      busy={actionBusy}
                      onEdit={openEditObservation}
                      onDelete={handleDeleteObservation}
                      selectedRowKey={selectedCardKey}
                      onRowHover={(rowKey, boxIds) =>
                        handleCardHover(rowKey, boxIds)
                      }
                      onRowClick={(rowKey, boxIds) =>
                        handleCardClick(rowKey, boxIds)
                      }
                    />
                  </section>

                  <section className="border-t border-black/10 pt-3 text-xs text-off-black-light/70 dark:border-white/10 dark:text-primary-off-white/60">
                    <p className="truncate" title={deploymentName || undefined}>
                      <span className="text-off-black-light/60 dark:text-primary-off-white/40">
                        {t("Deployment") + ": "}
                      </span>
                      {deploymentName || t("unknown")}
                    </p>
                    <p className="truncate" title={locationName || undefined}>
                      <span className="text-off-black-light/60 dark:text-primary-off-white/40">
                        {t("Location") + ": "}
                      </span>
                      {locationName || t("unknown")}
                    </p>
                    <p className="truncate" title={sensorName || undefined}>
                      <span className="text-off-black-light/60 dark:text-primary-off-white/40">
                        {t("Sensor") + ": "}
                      </span>
                      {sensorName || t("unknown")}
                    </p>
                  </section>
                </>
              )}
            </div>

            {/* Commit action bar (§6.2.1). Mark-image-blank moved into the
                ACTIONS block above (AKL-698); this bar now only carries the
                per-photo Save / Save & Next / Skip controls. */}
            {detail && !loading && (
              <div className="border-t border-black/10 p-3 dark:border-white/10">
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={`relative flex flex-1 items-center justify-center gap-2 rounded bg-black/10 px-2 py-1.5 text-sm font-semibold text-off-black-light hover:bg-black/20 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none dark:bg-white/10 dark:text-primary-off-white dark:hover:bg-white/20 ${
                      busyAction?.kind === "save" ? "" : "disabled:opacity-40"
                    }`}
                    onClick={() => doSave(false)}
                    disabled={actionBusy}
                    // AKL-779 follow-up: Save has more than one binding, so the
                    // chip shows the reachable one and the title carries the
                    // full set. `Ctrl/Cmd` follows the shortcuts help panel's
                    // existing convention of naming both modifiers rather than
                    // detecting the platform - the German catalogue already
                    // translates it as `Strg/Cmd`.
                    aria-keyshortcuts="s Control+s Meta+s"
                    title={t("S or Ctrl/Cmd+S")}
                    data-testid="save"
                  >
                    {/* Keep the label in layout (hidden) while its own spinner
                        shows, so the button doesn't resize; spinner overlays it.
                        spinnerColor is theme-aware, so it stays visible on the
                        translucent light/dark button. */}
                    <span
                      className={
                        busyAction?.kind === "save" ? "invisible" : undefined
                      }
                    >
                      {t("Save")}
                    </span>
                    {/* Held in layout while the spinner shows, like the label
                        above and the ACTIONS chips, so the button never resizes
                        mid-commit. */}
                    <kbd
                      aria-hidden="true"
                      className={`rounded bg-black/20 px-1.5 py-0.5 font-mono text-[10px] ${
                        busyAction?.kind === "save" ? "invisible" : ""
                      }`}
                    >
                      S
                    </kbd>
                    {busyAction?.kind === "save" && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <BeatLoader
                          color={spinnerColor}
                          loading={true}
                          size={8}
                          margin={3}
                        />
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`relative flex flex-1 items-center justify-center gap-2 rounded bg-sky-blue-dark px-2 py-1.5 text-sm font-semibold text-white hover:opacity-90 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none ${
                      busyAction?.kind === "save-next"
                        ? ""
                        : "disabled:opacity-40"
                    }`}
                    onClick={() => doSave(true)}
                    disabled={actionBusy}
                    // Shift is the one modifier whose glyph means the same on
                    // every platform, so the chip can carry it; Enter is the
                    // alternate and lives in the title.
                    aria-keyshortcuts="Shift+s Enter"
                    title={t("Shift+S or Enter")}
                    data-testid="save-next"
                  >
                    {/* White spinner reads on the sky-blue-dark fill (same in
                        both themes); label held in layout to keep button size. */}
                    <span
                      className={
                        busyAction?.kind === "save-next"
                          ? "invisible"
                          : undefined
                      }
                    >
                      {t("Save & Next")}
                    </span>
                    <kbd
                      aria-hidden="true"
                      className={`rounded bg-black/20 px-1.5 py-0.5 font-mono text-[10px] ${
                        busyAction?.kind === "save-next" ? "invisible" : ""
                      }`}
                    >
                      &#8679;S
                    </kbd>
                    {busyAction?.kind === "save-next" && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        <BeatLoader
                          color="#FFFFFF"
                          loading={true}
                          size={8}
                          margin={3}
                        />
                      </span>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bottom thumbnail queue */}
        <ReviewQueueStrip
          images={
            optimisticallyReviewedImageIds.size === 0 &&
            boxOverridesByImageId.size === 0
              ? images
              : images.map((img) => {
                  const override = boxOverridesByImageId.get(img.id);
                  return {
                    ...img,
                    ...(optimisticallyReviewedImageIds.has(img.id) && {
                      review_status: "reviewed" as const,
                    }),
                    ...(override && {
                      boxes: override.boxes,
                      frameLabels: override.frameLabels,
                    }),
                  };
                })
          }
          currentIndex={currentIndex}
          position={readoutPosition}
          total={readoutTotal}
          canStepPrev={canStepPrev}
          canStepNext={canStepNext}
          onSelect={handleSelectIndex}
          onStep={handleStep}
        />

        {selectedBoxPredictions.length > 0 && popoverAnchor && (
          <PredictionPopover
            predictions={selectedBoxPredictions}
            labelSourcePredictionId={selectedBox?.sourcePredictionId}
            selectedTaxonId={selectedBoxCandidateTaxon ?? null}
            // AKL-774/777: read-only while assign-species mode is on, for the
            // same reason its object actions are withheld - a choice made
            // mid-selection would strand the ticks behind a detail refresh.
            // AKL-712: an already-committed box has nothing to pick either;
            // its candidates are informational.
            onSelectTaxon={
              assignMode || selectedBoxReviewed?.rejected === false
                ? undefined
                : (taxonId, displayName) => {
                    if (selectedBoxId == null) return;
                    setCandidateTaxonByBox((prev) => ({
                      ...prev,
                      [selectedBoxId]: { taxonId, displayName },
                    }));
                  }
            }
            anchor={popoverAnchor}
            reviewed={selectedBoxReviewed}
            unresolved={selectedBoxUnresolved}
            busy={actionBusy}
            pending={
              busyAction?.kind === "object-accept"
                ? "accept"
                : busyAction?.kind === "object-reject"
                  ? "reject"
                  : null
            }
            onClose={() => {
              setSelectedBoxId(null);
              setPopoverAnchor(null);
            }}
            // AKL-774: read-only while assign-species mode is on. The popover
            // still opens so a box can be inspected mid-selection, but its
            // object actions are withheld - committing one here would bypass
            // the selection being built and strand the ticks behind a detail
            // refresh. PredictionPopover renders each button only when its
            // handler is supplied, so undefined is all it takes.
            // AKL-717: an unresolved box proposes no species, so there is
            // nothing to accept — the popover hides Accept, and no handler is
            // wired so a stray click could never reach acceptSuggestion.
            onAccept={
              assignMode || selectedBoxUnresolved
                ? undefined
                : () => {
                    if (!selectedBox || selectedBoxPredictions.length === 0)
                      return;
                    // Accept needs the box's current taxon and the aggregate its count
                    // leaves. Both come from the box's owning item (AKL-703) — reading
                    // them off the prediction fails for an already-reviewed box.
                    const { taxonId: boxTaxonId, parentTaxonId } =
                      boxActionContext(selectedBox);
                    // AKL-777: a species picked in the popover overrides the
                    // box's own taxon. With nothing picked this is exactly the
                    // taxon it always was, so Accept is unchanged by default.
                    const taxonId = selectedBoxCandidateTaxon ?? boxTaxonId;
                    if (taxonId == null) {
                      toast.error(t("Action failed. Please try again."));
                      return;
                    }
                    runAction(
                      () =>
                        acceptSuggestion(
                          mediaId,
                          [selectedBox.predictionId],
                          taxonId,
                          "object",
                          parentTaxonId,
                        ),
                      { kind: "object-accept" },
                    );
                  }
            }
            onReject={
              assignMode
                ? undefined
                : () =>
                    selectedBox &&
                    runAction(
                      () =>
                        rejectSuggestion(
                          mediaId,
                          [selectedBox.predictionId],
                          "object",
                          boxActionContext(selectedBox).parentTaxonId,
                        ),
                      { kind: "object-reject" },
                    )
            }
            // AKL-717: on an unresolved box "Edit" becomes "Assign species" —
            // the same slot, opening the editor with nothing preselected.
            onEdit={
              assignMode
                ? undefined
                : selectedBoxUnresolved
                  ? () => selectedBox && openAssignSpeciesToBox(selectedBox)
                  : () => selectedBox && openEditObject(selectedBox)
            }
            onDelete={() => {
              if (!selectedBox) return;
              const obs = (detail?.observations ?? []).find(
                (o) => String(o.id) === selectedBox.ownerKey,
              );
              if (obs) handleDeleteObservation(obs);
            }}
          />
        )}

        {editor && (
          <ObservationEditor
            title={editor.title}
            projectId={projectId}
            initial={editor.initial}
            busy={actionBusy}
            countHelp={editor.countHelp}
            seededSpeciesOptions={editor.speciesOptions}
            onConfirm={handleEditorConfirm}
            onCancel={() => setEditor(null)}
          />
        )}

        {confirm && (
          <ConfirmationModal
            isOpen={true}
            confirmationMessage={confirm.message}
            confirmLabel={confirm.confirmLabel}
            confirmIcon={
              confirm.kind === "zero-obs" || confirm.kind === "mark-blank"
                ? "⊘"
                : undefined
            }
            saving={actionBusy}
            onConfirm={confirm.onConfirm}
            onCancel={() => setConfirm(null)}
            title={
              confirm.kind === "zero-obs"
                ? t("Save with no observations?")
                : undefined
            }
            icon={
              confirm.kind === "zero-obs" ? <QuestionCircleIcon /> : undefined
            }
            confirmHotkeyLabel={confirm.kind === "zero-obs" ? "B" : undefined}
            // Only zero-obs opens after a request; the other kinds open from a
            // still-enabled control, which ModalDialog returns focus to itself.
            returnFocusRef={
              confirm.kind === "zero-obs" ? commitOpenerRef : undefined
            }
            className={
              confirm.kind === "zero-obs" || confirm.kind === "mark-blank"
                ? "max-w-sm"
                : undefined
            }
            // All three confirm kinds (delete-obs, mark-blank, zero-obs)
            // discard work or are irreversible — every one gets the
            // destructive treatment.
            danger={true}
          />
        )}

        {showHelp && (
          <div
            className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/60"
            onClick={() => setShowHelp(false)}
            data-testid="shortcut-help"
          >
            <div
              className="max-h-[80%] w-[420px] overflow-y-auto rounded-lg bg-primary-off-white p-5 text-off-black-light shadow-2xl dark:bg-storm-blue-dark dark:text-primary-off-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-semibold">{t("Keyboard shortcuts")}</h2>
                <button
                  type="button"
                  className="rounded p-1 focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none"
                  onClick={() => setShowHelp(false)}
                  aria-label={t("Close")}
                >
                  ✕
                </button>
              </div>
              <ul className="flex flex-col gap-1 text-sm">
                <li>{t("→ / ←: Next / Previous photo")}</li>
                <li>
                  {t("S or Ctrl/Cmd+S: Save · Shift+S / Enter: Save & Next")}
                </li>
                <li>{t("A: Accept observations (or the focused card)")}</li>
                <li>{t("B: Mark image blank")}</li>
                <li>{t("N: Add observation")}</li>
                <li>{t("M: Select suggestions")}</li>
                <li>{t("A / R: Accept / Reject the selected suggestions")}</li>
                {/* AKL-779: its own line rather than folded into the Esc entry
                    below. The mode's three keys then read together (M in, A/R
                    commit, Esc out), and the existing Esc line stays true - it
                    describes what Esc does when the mode is off. */}
                <li>{t("Esc: Leave Select suggestions")}</li>
                <li>{t("R / E: Reject / Edit the focused card")}</li>
                <li>{t("Tab / Shift+Tab: Move between cards")}</li>
                <li>{t("Space: Toggle bounding boxes · + / -: Zoom")}</li>
                <li>{t("Esc: Close popover or the review view")}</li>
                <li>{t("? or H: Show this help")}</li>
              </ul>
            </div>
          </div>
        )}

        {/* Inline SVG filter for the sharpen toggle (AKL-681). Mounted while the
            surface is open so the browser can pre-compose the filter graph; the
            <img> only references it via CSS `filter: url(#akili-sharpen)` when
            sharpen is on (see buildImageFilter). Standard 3x3 Laplacian kernel;
            preserveAlpha keeps transparent edges from ringing. */}
        <svg
          width={0}
          height={0}
          style={{ position: "absolute" }}
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <filter id={SHARPEN_FILTER_ID}>
              <feConvolveMatrix
                order="3"
                preserveAlpha="true"
                kernelMatrix="0 -1 0  -1 5 -1  0 -1 0"
              />
            </filter>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default FullScreenImageReview;
